import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  ActionIcon,
  Accordion,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  MultiSelect,
  Modal,
  Menu,
  SegmentedControl,
  NativeSelect,
  NumberInput,
  Paper,
  Popover,
  Progress,
  Rating,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  IconAlertTriangle,
  IconChartBar,
  IconCheck,
  IconCopy,
  IconDeviceFloppy,
  IconDownload,
  IconEye,
  IconEyeOff,
  IconExternalLink,
  IconHistory,
  IconMenu2,
  IconPlayerPlay,
  IconPlus,
  IconRefresh,
  IconRotateClockwise,
  IconSettings,
  IconTrash,
} from '@/components/icons'
import { EmptyState, InlineLoader, PageCenterLoader, PageShell } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import { startQualityPolling } from '@/lib/quality-polling'
import { useAuth } from '@/lib/auth-context'
import { formatDateTimeDot, ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import {
  isChatSystemTimelineItem,
  mergeMessagesWithTimelineSystemEvents,
} from '@/components/tickets/ticket-assignment-system-messages'
import { MessageBubble } from '@/components/tickets/TicketMessageThread'
import {
  SHOW_ASSIGNMENT_SYSTEM_MESSAGES_KEY,
  readShowAssignmentSystemMessages,
} from '@/components/tickets/ticket-detail-page-helpers'
import { resolveQuotedForMessage } from '@/components/tickets/ticket-message-utils'
import {
  hasPermission,
  loadMyPermissions,
  mergeWithDefaultPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import type {
  MyPermissionsResponse,
  ProjectRow,
  QualityAISettings,
  QualityCriterion,
  QualityCheckItemRow,
  QualityIncorrectResponseRow,
  QualityIncorrectResponsesResponse,
  QualityMessageRow,
  QualityScoreValue,
  QualityReportResponse,
  QualitySessionRow,
  QualitySessionCreatePayload,
  QualitySessionPreview,
  QualitySettingsResponse,
  QualityTargetRow,
  TicketDetailMessage,
  TicketTimelineItem,
  VLLMGatewayModel,
} from '@/types/api'

import './QualityArmPage.css'

const SCORE_HINTS = ['не оценено', 'критично', 'плохо', 'нормально', 'хорошо', 'отлично']
const WEIGHTED_CHOICE_LABELS: Record<string, string> = {
  yes: 'Да',
  no: 'Нет',
  na: 'н/д',
  skip: 'Пропущено',
}
const QUALITY_ANONYMOUS_ROLE_OPTIONS = [
  { value: 'operator', label: 'Операторы' },
  { value: 'trainee', label: 'Стажеры' },
  { value: 'shift_lead', label: 'Старшие смены' },
  { value: 'npc_helper', label: 'НПЦ помощники' },
  { value: 'human_helper', label: 'Помощники' },
  { value: 'analyst', label: 'Аналитики' },
  { value: 'admin', label: 'Администраторы' },
  { value: 'owner', label: 'Владелец' },
]
const QUALITY_ANONYMOUS_ROLE_LABELS = Object.fromEntries(
  QUALITY_ANONYMOUS_ROLE_OPTIONS.map((item) => [item.value, item.label]),
)

type QualityStandardPreviewTarget = 'operators' | 'bot'

function qualityAnonymousRolesLabel(roles?: string[] | null): string {
  const values = roles?.length ? roles : ['operator']
  return values.map((role) => QUALITY_ANONYMOUS_ROLE_LABELS[role] || role).join(', ')
}

function criterionComparable(criterion: QualityCriterion): string {
  return JSON.stringify({
    key: criterion.key,
    label: criterion.label,
    weight: criterion.weight,
    enabled: criterion.enabled,
    include_in_total: criterion.include_in_total !== false,
    allow_skip: criterion.allow_skip !== false,
    type: criterion.type,
    scope: criterion.scope || 'operator',
    option_weights: criterion.option_weights || {},
  })
}

function criterionShortDetails(criterion?: QualityCriterion | null): string {
  if (!criterion) return '—'
  const enabled = criterion.enabled ? 'вкл' : 'выкл'
  const total = criterion.include_in_total !== false ? 'в итог' : 'не в итог'
  const skip = criterion.allow_skip !== false ? 'можно пропустить' : 'без пропуска'
  return `${criterion.type} · ${criterion.scope || 'operator'} · вес ${criterion.weight} · ${enabled} · ${total} · ${skip}`
}

function criterionCompareStatus(
  current?: QualityCriterion,
  standard?: QualityCriterion,
): { label: string; color: string } {
  if (!current && standard) return { label: 'Добавится', color: 'green' }
  if (current && !standard) return { label: 'Удалится', color: 'red' }
  if (current && standard && criterionComparable(current) !== criterionComparable(standard)) {
    return { label: 'Изменится', color: 'orange' }
  }
  return { label: 'Без изменений', color: 'gray' }
}

function optionWeight(criterion: QualityCriterion, option: 'yes' | 'no' | 'na'): number {
  const value = criterion.option_weights?.[option]
  return typeof value === 'number' ? value : option === 'yes' ? criterion.weight : 0
}

function scoreColor(score?: number | null): string {
  if (score == null) return 'gray'
  if (score >= 90) return 'green'
  if (score >= 75) return 'lime'
  if (score >= 60) return 'yellow'
  return 'red'
}

function formatQualityPreviewTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

function formatQualityDuration(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—'
  const total = Math.max(0, Math.round(seconds))
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  if (days) return `${days} д ${hours} ч`
  if (hours) return `${hours} ч ${minutes} мин`
  if (minutes) return `${minutes} мин`
  return `${secs} сек`
}

const QUALITY_SCENARIO_LABELS: Record<string, string> = {
  greeting_only: 'Только приветствие',
  clear_request_resolved: 'Понятный запрос решен',
  clarification_pending: 'Ожидание ответа на уточняющий вопрос',
  customer_silent_after_answer: 'Клиент не ответил после ответа',
  unresolved_active: 'Запрос требует продолжения',
  transfer_or_handoff: 'Передача между участниками',
  multi_operator: 'Несколько операторов',
  system_or_technical: 'Системный или технический сценарий',
  other: 'Другой сценарий',
}

function QualitySummaryBlock({ summary, compact = false }: { summary?: QualitySessionRow['summary']; compact?: boolean }) {
  if (!summary) return null
  const ai = summary.ai_summary
  const score = ai?.status === 'completed' && ai.overall_score != null ? ai.overall_score : summary.average_score
  const riskColor = ai?.risk_level === 'high' ? 'red' : ai?.risk_level === 'medium' ? 'orange' : 'green'
  const generalRecommendation = ai?.recommendations?.length
    ? ai.recommendations.join('\n')
    : summary.recommendation
  return (
    <Paper withBorder p={compact ? 'xs' : 'sm'} radius="sm">
      <Group justify="space-between" align="center" gap="xs" wrap="wrap">
        <Text fw={700} size={compact ? 'sm' : 'md'}>Общее заключение</Text>
        <Group gap="xs">
          <Badge color={scoreColor(score)} variant="light">Итог: {score != null ? `${score}%` : '—'}</Badge>
          {ai?.status === 'completed' && ai.risk_level ? <Badge color={riskColor} variant="light">Риск: {ai.risk_level === 'high' ? 'высокий' : ai.risk_level === 'medium' ? 'средний' : 'низкий'}</Badge> : null}
          {summary.ai_summary_status === 'queued' ? <Badge color="blue" variant="light">AI-заключение готовится</Badge> : null}
          {summary.ai_summary_status === 'failed' ? <Badge color="orange" variant="light">AI-заключение не сформировано</Badge> : null}
        </Group>
      </Group>
      <Text size="sm" mt="xs" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {ai?.status === 'completed' && ai.conclusion ? ai.conclusion : summary.recommendation || 'Итог появится после оценки обращений.'}
      </Text>
      <Box mt="sm">
        <Text size="xs" fw={700} c="orange">Общая рекомендация</Text>
        <Text size="sm" mt={3} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {generalRecommendation || 'Рекомендация появится после завершения проверки.'}
        </Text>
      </Box>
      {ai?.status === 'completed' && (ai.strengths?.length || ai.recommendations?.length) ? (
        <Group align="flex-start" gap="lg" mt="sm" wrap="wrap">
          {ai.strengths?.length ? <Box style={{ flex: '1 1 260px' }}><Text size="xs" fw={700} c="green">Сильные стороны</Text><Stack gap={2} mt={3}>{ai.strengths.map((value, index) => <Text size="sm" key={`strength-${index}`}>• {value}</Text>)}</Stack></Box> : null}
          {ai.recommendations?.length ? <Box style={{ flex: '1 1 300px' }}><Text size="xs" fw={700} c="orange">Рекомендации</Text><Stack gap={2} mt={3}>{ai.recommendations.map((value, index) => <Text size="sm" key={`recommendation-${index}`}>• {value}</Text>)}</Stack></Box> : null}
        </Group>
      ) : null}
      {!compact && summary.by_criteria?.length ? (
        <Box mt="sm">
          <Text size="xs" fw={700} c="dimmed" mb={4}>Оценки по всем параметрам</Text>
          <Group gap="xs" wrap="wrap">
            {summary.by_criteria.map((criterion) => {
              if (criterion.average == null) {
                return (
                  <Badge key={criterion.key} variant="outline" color="gray">
                    {criterion.label}: —
                  </Badge>
                )
              }
              const percent = criterion.average_percent ?? (criterion.type === 'boolean' || criterion.type === 'weighted_choice' ? criterion.average : (criterion.average || 0) * 20)
              return (
                <Badge key={criterion.key} variant="outline" color={scoreColor(percent)}>
                  {criterion.label}: {criterion.type === 'boolean' || criterion.type === 'weighted_choice' ? `${criterion.average}%` : `${criterion.average}/5 · ${percent}%`}
                </Badge>
              )
            })}
          </Group>
        </Box>
      ) : null}
      {!compact && summary.operator_scope?.items_with_multiple_operators ? (
        <Text size="xs" c="dimmed" mt="sm">
          Несколько операторов: {summary.operator_scope.items_with_multiple_operators} из {summary.operator_scope.items_with_operator_responses || 0} обращений ({summary.operator_scope.multiple_operator_share_percent ?? 0}%). Индивидуальные оценки относятся к выбранному оператору, общий результат — ко всему обращению.
        </Text>
      ) : null}
    </Paper>
  )
}

function QualityTicketTimeMetricsBlock({ item }: { item?: QualityCheckItemRow | null }) {
  const metrics = item?.time_metrics
  const rows = [
    {
      label: 'Общее время решения',
      value: formatQualityDuration(metrics?.resolution_seconds),
      hint: 'От создания обращения до закрытия или решения',
    },
    {
      label: 'Время первого ответа',
      value: formatQualityDuration(metrics?.assignment_to_first_reply_seconds),
      hint: 'От назначения на оператора до первого ответа, без Мозгобота',
    },
    {
      label: 'Среднее время ответа',
      value: formatQualityDuration(metrics?.average_operator_reply_seconds),
      hint: `После сообщений клиента до ответа оператора${metrics?.operator_reply_samples ? ` · ${metrics.operator_reply_samples} замеров` : ''}`,
    },
  ]
  return (
    <Paper withBorder radius="sm" p="xs" className="quality-ticket-time-metrics">
      <Stack gap={6}>
        <Text fw={700} size="sm">Времена обращения</Text>
        {rows.map((row) => (
          <Box key={row.label} className="quality-ticket-time-metric">
            <Group justify="space-between" gap="xs" wrap="nowrap" align="flex-start">
              <Text size="xs" c="dimmed" style={{ minWidth: 0 }}>
                {row.label}
              </Text>
              <Text size="sm" fw={700} style={{ flexShrink: 0 }}>
                {row.value}
              </Text>
            </Group>
            <Text size="xs" c="dimmed" lineClamp={2}>{row.hint}</Text>
          </Box>
        ))}
      </Stack>
    </Paper>
  )
}

function AiRequestView({ request }: { request: QualityCheckItemRow['ai_request'] }) {
  const batches = request?.batches?.length
    ? request.batches
    : request?.messages?.length
      ? [{ batch_number: 1, total_batches: 1, messages: request.messages }]
      : []
  if (!batches.length) return <Text size="sm" c="dimmed">Запрос ещё не сформирован.</Text>
  return (
    <Stack gap="xs">
      <Group gap="xs" wrap="wrap">
        {request?.model ? <Badge variant="light">Модель: {request.model}</Badge> : null}
        {request?.assistant_id ? <Badge variant="light">Assistant: {request.assistant_id}</Badge> : null}
        <Badge variant="light">Частей диалога: {batches.length}</Badge>
      </Group>
      <Accordion multiple variant="separated" radius="sm" defaultValue={[`batch-${batches[0].batch_number}`]}>
        {batches.map((batch) => {
          const system = batch.messages?.find((message) => message.role === 'system')?.content || ''
          const user = batch.messages?.find((message) => message.role === 'user')?.content || ''
          const value = `batch-${batch.batch_number}`
          return (
            <Accordion.Item key={`${batch.batch_number}-${batch.total_batches}`} value={value}>
              <Accordion.Control>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={700}>Часть {batch.batch_number} из {batch.total_batches}</Text>
                  <Badge size="sm" variant="light">Отправлено модели</Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Box>
                    <Text size="xs" c="dimmed" fw={600} mb={4}>Инструкции модели</Text>
                    <Paper withBorder p="sm" radius="sm" bg="var(--mantine-color-gray-light)">
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{system || '—'}</Text>
                    </Paper>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed" fw={600} mb={4}>Фрагмент диалога</Text>
                    <Paper withBorder p="sm" radius="sm">
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{user || '—'}</Text>
                    </Paper>
                  </Box>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )
        })}
      </Accordion>
    </Stack>
  )
}

function formatCriterionScore(criterion: QualityCriterion, value?: QualityScoreValue): string {
  if (value == null) return '—'
  if (value === 'skip') return 'Пропущено'
  if (criterion.type === 'weighted_choice') return WEIGHTED_CHOICE_LABELS[String(value)] || '—'
  if (criterion.type === 'boolean') return value ? 'Да' : 'Нет'
  return typeof value === 'number' ? `${value}/5` : '—'
}

function CriteriaScoresList({
  criteria,
  scores,
  notApplicable = [],
}: {
  criteria: QualityCriterion[]
  scores: Record<string, QualityScoreValue>
  notApplicable?: string[]
}) {
  const enabled = criteria.filter((criterion) => criterion.enabled)
  if (!enabled.length) return <Text size="sm" c="dimmed">Критерии не настроены</Text>
  const na = new Set(notApplicable.map(String))
  return (
    <Stack gap={3}>
      {enabled.map((criterion) => {
        const hasScore = scores[criterion.key] != null
        const isSkipped = scores[criterion.key] === 'skip'
        const isNa = na.has(criterion.key)
        return (
          <Group key={criterion.key} justify="space-between" gap="xs" wrap="nowrap">
            <Text size="sm" style={{ minWidth: 0 }} truncate>{criterion.label}</Text>
            <Badge
              color={isNa || isSkipped ? 'gray' : hasScore ? (criterion.type === 'boolean' ? (scores[criterion.key] ? 'green' : 'gray') : criterion.type === 'weighted_choice' ? (scores[criterion.key] === 'yes' ? 'green' : scores[criterion.key] === 'no' ? 'red' : 'gray') : 'blue') : 'orange'}
              variant="light"
            >
              {isNa ? 'Не применимо' : formatCriterionScore(criterion, hasScore ? scores[criterion.key] : null)}
            </Badge>
          </Group>
        )
      })}
    </Stack>
  )
}

function AiResponseView({ item, criteria }: { item?: QualityCheckItemRow | null; criteria: QualityCriterion[] }) {
  const result = item?.ai_response_json
  const scores = {
    ...(item?.scores || {}),
    ...(result && typeof result.scores === 'object' && result.scores ? result.scores as Record<string, QualityScoreValue> : {}),
  }
  const notApplicable = result && Array.isArray(result.not_applicable) ? result.not_applicable.map(String) : []
  const scenario = typeof result?.scenario === 'string' ? result.scenario : null
  const outcome = typeof result?.outcome === 'string' ? result.outcome : null
  const evidence = result && typeof result.criterion_evidence === 'object' && result.criterion_evidence
    ? result.criterion_evidence as Record<string, { evidence?: string; reason?: string; applicable?: boolean }>
    : {}
  const comment = typeof result?.comment === 'string' ? result.comment : item?.comment
  const recommendation = typeof result?.recommendation === 'string' ? result.recommendation : item?.recommendation
  const transfer = result && typeof result.transfer_assessment === 'object' && result.transfer_assessment
    ? result.transfer_assessment as { cause?: string; initiated_by_selected_operator?: boolean; avoidable_by_selected_operator?: boolean; note?: string }
    : null
  const transferCauseLabels: Record<string, string> = {
    none: 'передачи не было',
    ai_handoff: 'передача Мозгобота оператору',
    shift_end: 'окончание смены',
    operator_unavailable: 'оператор недоступен',
    inactivity: 'бездействие оператора',
    automatic_redistribution: 'автоматическое перераспределение',
    manual_reassignment: 'ручное переназначение',
    unknown: 'причина не определена',
  }
  if (!item?.ai_response_json && !item?.ai_response && !item?.ai_error) return <Text size="sm" c="dimmed">Ответ ещё не получен.</Text>
  return (
    <Stack gap="xs">
      {scenario || outcome ? (
        <Paper withBorder p="xs" radius="sm">
          <Text size="xs" fw={700}>Сценарий обращения</Text>
          {scenario ? <Text size="sm" mt={3}>Тип: {QUALITY_SCENARIO_LABELS[scenario] || scenario}</Text> : null}
          {outcome ? <Text size="sm" mt={3} style={{ whiteSpace: 'pre-wrap' }}>{outcome}</Text> : null}
        </Paper>
      ) : null}
      <Paper withBorder p="xs" radius="sm">
        <Group justify="space-between" gap="xs" wrap="wrap" mb={4}>
          <Text size="xs" fw={700}>Оценки по параметрам</Text>
          {item?.weighted_score != null ? (
            <Badge color={scoreColor(item.weighted_score)} variant="light">Итог: {item.weighted_score}%</Badge>
          ) : null}
        </Group>
        <CriteriaScoresList criteria={criteria} scores={scores} notApplicable={notApplicable} />
      </Paper>
      {notApplicable.length ? (
        <Box>
          <Text size="xs" c="dimmed">Не применимо к этому обращению</Text>
          <Text size="sm">{criteria.filter((criterion) => notApplicable.includes(criterion.key)).map((criterion) => criterion.label).join(', ') || notApplicable.join(', ')}</Text>
        </Box>
      ) : null}
      {Object.keys(evidence).length ? (
        <Accordion variant="separated" radius="sm">
          <Accordion.Item value="criterion-evidence">
            <Accordion.Control><Text size="sm" fw={600}>Основания оценок</Text></Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {criteria.filter((criterion) => evidence[criterion.key]).map((criterion) => (
                  <Box key={criterion.key}>
                    <Text size="xs" fw={700}>{criterion.label}</Text>
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{evidence[criterion.key]?.evidence || 'Факт не указан'}</Text>
                    {evidence[criterion.key]?.reason ? <Text size="xs" c="dimmed">{evidence[criterion.key]?.reason}</Text> : null}
                  </Box>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ) : null}
      <Box><Text size="xs" c="dimmed">Комментарий модели</Text><Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{comment || '—'}</Text></Box>
      <Box><Text size="xs" c="dimmed">Рекомендация</Text><Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{recommendation || '—'}</Text></Box>
      {item?.operator_weighted_score != null || item?.ticket_weighted_score != null ? (
        <Group gap="xs" wrap="wrap">
          {item.operator_weighted_score != null ? <Badge color={scoreColor(item.operator_weighted_score)} variant="light">Вклад оператора: {item.operator_weighted_score}%</Badge> : null}
          {item.ticket_weighted_score != null ? <Badge color={scoreColor(item.ticket_weighted_score)} variant="light">Оценка тикета: {item.ticket_weighted_score}%</Badge> : null}
        </Group>
      ) : null}
      {transfer ? (
        <Paper withBorder p="xs" radius="sm">
          <Text size="xs" fw={700}>Контекст передачи</Text>
          <Text size="sm" mt={4}>
            Причина: {transferCauseLabels[transfer.cause || ''] || transfer.cause || 'не определена'} · {transfer.initiated_by_selected_operator ? 'инициирована выбранным оператором' : 'не инициирована выбранным оператором'}
            {transfer.avoidable_by_selected_operator ? ' · могла быть предотвращена оператором' : ' · не является ошибкой выбранного оператора'}
          </Text>
          {transfer.note ? <Text size="sm" mt={4} style={{ whiteSpace: 'pre-wrap' }}>{transfer.note}</Text> : null}
        </Paper>
      ) : null}
      {item?.ai_error ? (
        <Alert color={item.ai_status === 'queued' ? 'orange' : 'red'} title={item.ai_status === 'queued' ? 'Повторная попытка запланирована' : 'Ошибка'}>
          {item.ai_error}
        </Alert>
      ) : null}
      {!result && item?.ai_response ? (
        <Box><Text size="xs" c="dimmed">Текстовый ответ модели</Text><Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.ai_response}</Text></Box>
      ) : null}
      {item?.ai_response && result ? (
        <Accordion variant="contained" radius="sm">
          <Accordion.Item value="raw-ai">
            <Accordion.Control><Text size="sm">Сырой ответ модели</Text></Accordion.Control>
            <Accordion.Panel><Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{item.ai_response}</Text></Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ) : null}
    </Stack>
  )
}

function qualityMessageToTicketDetailMessage(
  message: QualityMessageRow,
  ticketId: string,
): TicketDetailMessage {
  return {
    id: message.id,
    ticket_id: ticketId,
    user_id: message.sender_id ?? null,
    message_type: message.message_type,
    content: message.content,
    content_locale: message.content_locale ?? null,
    content_translated: message.content_translated ?? null,
    outbound_content: message.outbound_content ?? null,
    channel_delivery_status: message.channel_delivery_status ?? null,
    delivery_error: message.delivery_error ?? null,
    sender_name: message.sender_name ?? null,
    sender_email: message.sender_email ?? null,
    is_internal: Boolean(message.is_internal),
    is_bulk: Boolean(message.is_bulk),
    is_ai_assistant: Boolean(message.is_ai_assistant),
    is_quality_system: Boolean(message.is_quality_system),
    quote_message_id: message.quote_message_id ?? null,
    created_at: message.created_at ?? null,
    channel_name: message.channel_name ?? null,
    channel_type: message.channel_type ?? null,
  }
}

function QualityConversation({
  item,
  onIncorrectResponseCreated,
}: {
  item?: QualityCheckItemRow | null
  onIncorrectResponseCreated?: (itemId: string, mark: QualityIncorrectResponseRow) => void
}) {
  const [showSystemMessages, setShowSystemMessages] = useState(readShowAssignmentSystemMessages)
  const [timelineItems, setTimelineItems] = useState<TicketTimelineItem[]>([])
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [timelineError, setTimelineError] = useState<string | null>(null)
  const [incorrectCommentByMessage, setIncorrectCommentByMessage] = useState<Record<string, string>>({})
  const [incorrectPopoverMessageId, setIncorrectPopoverMessageId] = useState<string | null>(null)
  const [incorrectSavingMessageId, setIncorrectSavingMessageId] = useState<string | null>(null)

  const ticketId = item?.ticket_id || item?.ticket?.id || null

  const toggleSystemMessages = useCallback(() => {
    setShowSystemMessages((current) => {
      const next = !current
      try {
        localStorage.setItem(SHOW_ASSIGNMENT_SYSTEM_MESSAGES_KEY, next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  useEffect(() => {
    setTimelineItems([])
    setTimelineError(null)
  }, [ticketId])

  useEffect(() => {
    if (!showSystemMessages || !ticketId) return
    let cancelled = false
    setTimelineLoading(true)
    setTimelineError(null)
    api.ticketTimeline(ticketId)
      .then((res) => {
        if (cancelled) return
        setTimelineItems((res.items || []).filter(isChatSystemTimelineItem))
      })
      .catch((requestError) => {
        if (cancelled) return
        setTimelineItems([])
        setTimelineError(
          requestError instanceof ApiError
            ? requestError.message
            : 'Не удалось загрузить события обращения',
        )
      })
      .finally(() => {
        if (!cancelled) setTimelineLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [showSystemMessages, ticketId])

  const messages = useMemo(() => {
    if (!item?.messages?.length) return []
    const detailMessages = item.messages.map((message) =>
      qualityMessageToTicketDetailMessage(message, ticketId || item.ticket_id),
    )
    if (!showSystemMessages || !ticketId) return detailMessages
    return mergeMessagesWithTimelineSystemEvents(detailMessages, timelineItems, ticketId)
  }, [item?.messages, item?.ticket_id, showSystemMessages, ticketId, timelineItems])
  const messageById = useMemo(() => new Map(messages.map((message) => [message.id, message])), [messages])
  const incorrectMarkedMessageIds = useMemo(
    () => new Set((item?.incorrect_responses || []).map((mark) => mark.message_id)),
    [item?.incorrect_responses],
  )

  if (!item?.messages?.length) {
    return <EmptyState title="Нет сообщений" description="По обращению не найден публичный диалог." />
  }
  const selectedOperator = item.selected_operator
  const participants = item.operator_participants || []
  const otherOperators = participants.filter((operator) => operator.id !== selectedOperator?.id)
  const ticketTitle = item.ticket
    ? `#${item.ticket.ticket_number || item.position} · ${item.ticket.subject || 'Без темы'}`
    : `Обращение #${item.position}`
  const ticketHref = item.ticket
    ? `${window.location.origin}/tickets/${encodeURIComponent(
        ticketPathSegmentFromListItem({
          id: item.ticket.id,
          ticket_number: item.ticket.ticket_number,
          project_slug: item.ticket.project_slug,
        }),
      )}`
    : null
  const canMarkIncorrectResponse = (message: TicketDetailMessage) => (
    message.message_type === 'operator' &&
    !message.is_internal &&
    !message.is_ai_assistant &&
    !message.is_quality_system &&
    (!selectedOperator?.id || message.user_id === selectedOperator.id)
  )
  const saveIncorrectResponse = (message: TicketDetailMessage) => {
    if (!item?.id) return
    const comment = (incorrectCommentByMessage[message.id] || '').trim()
    if (!comment) {
      showNotification({
        title: 'Комментарий обязателен',
        message: 'Опишите, что некорректно в ответе оператора.',
        color: 'orange',
      })
      return
    }
    setIncorrectSavingMessageId(message.id)
    void api.qualityCreateIncorrectResponse(item.session_id, item.id, {
      message_id: message.id,
      comment,
    }).then(
      (res) => {
        onIncorrectResponseCreated?.(item.id, res.item)
        setIncorrectCommentByMessage((prev) => ({ ...prev, [message.id]: '' }))
        setIncorrectPopoverMessageId(null)
        showNotification({
          title: 'Отметка сохранена',
          message: 'Ответ добавлен в список некорректных.',
          color: 'green',
        })
      },
      (e) => {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось сохранить отметку',
          color: 'red',
        })
      },
    ).finally(() => setIncorrectSavingMessageId(null))
  }
  const copyTicketLink = () => {
    if (!ticketHref) return
    void navigator.clipboard.writeText(ticketHref).then(
      () => {
        showNotification({
          title: 'Ссылка скопирована',
          message: 'Ссылка на обращение скопирована в буфер обмена.',
          color: 'green',
        })
      },
      () => {
        showNotification({
          title: 'Не удалось скопировать ссылку',
          message: ticketHref,
          color: 'red',
        })
      },
    )
  }
  return (
    <ScrollArea style={{ height: '100%', minHeight: 0 }} type="auto" offsetScrollbars="y">
      <Stack gap="xs" pr="xs">
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Box style={{ minWidth: 0 }}>
            <Group gap={4} wrap="nowrap" align="flex-start">
              <Text fw={700} size="sm" lineClamp={2} style={{ minWidth: 0, flex: '1 1 auto' }}>
                {ticketTitle}
              </Text>
              {ticketHref ? (
                <Tooltip label="Скопировать ссылку на обращение" withArrow>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    aria-label="Скопировать ссылку на обращение"
                    onClick={copyTicketLink}
                    style={{ flex: '0 0 auto' }}
                  >
                    <IconCopy size={16} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
            </Group>
            <Text size="xs" c="dimmed">
              {messages.length} сообщ. и событий
            </Text>
          </Box>
          <Popover position="bottom-end" withinPortal width={300}>
            <Popover.Target>
              <ActionIcon variant="subtle" size="sm" aria-label="Время решения и ответов" title="Время решения и ответов">
                <IconHistory size={16} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown><QualityTicketTimeMetricsBlock item={item} /></Popover.Dropdown>
          </Popover>
          <Tooltip
            label={
              showSystemMessages
                ? 'Скрыть системные события обращения'
                : 'Показать системные события обращения'
            }
          >
            <ActionIcon
              variant={showSystemMessages ? 'light' : 'subtle'}
              color={showSystemMessages ? 'teal' : undefined}
              size="sm"
              aria-label={
                showSystemMessages
                  ? 'Скрыть системные события обращения'
                  : 'Показать системные события обращения'
              }
              aria-pressed={showSystemMessages}
              loading={timelineLoading && showSystemMessages}
              onClick={toggleSystemMessages}
            >
              {showSystemMessages ? (
                <IconEye size={16} stroke={1.5} />
              ) : (
                <IconEyeOff size={16} stroke={1.5} />
              )}
            </ActionIcon>
          </Tooltip>
        </Group>
        {showSystemMessages && timelineError ? (
          <Alert color="red" variant="light" p="xs">
            <Text size="xs">{timelineError}</Text>
          </Alert>
        ) : null}
        {showSystemMessages && timelineLoading && !timelineItems.length ? (
          <Text size="xs" c="dimmed">
            Загрузка событий обращения...
          </Text>
        ) : null}
        {item.multiple_operators ? (
          <Paper withBorder radius="sm" p={6} bg="var(--mantine-color-orange-light)">
            <Group gap={6} wrap="wrap">
              <Badge size="xs" color="orange" variant="light">Несколько операторов</Badge>
              {selectedOperator ? <Text size="xs" fw={600}>Проверяется: {selectedOperator.name}</Text> : null}
              <Text size="xs" c="dimmed">
                Его сообщений: {item.selected_operator_message_count || 0}; остальных: {item.other_operator_message_count || 0}.
                {otherOperators.length ? ` Участники: ${otherOperators.map((operator) => operator.name).join(', ')}.` : ''}
              </Text>
            </Group>
          </Paper>
        ) : selectedOperator ? (
          <Paper withBorder radius="sm" p={6} bg="var(--mantine-color-blue-light)">
            <Group gap={6} wrap="wrap">
              <Badge size="xs" color="blue" variant="light">Проверка оператора</Badge>
              <Text size="xs" c="dimmed">
                Оцениваются ответы {selectedOperator.name}; итог решения смотрится по всему обращению.
              </Text>
            </Group>
          </Paper>
        ) : null}
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            m={message}
            currentUserId={undefined}
            users={[]}
            quoted={resolveQuotedForMessage(message, messageById)}
            messageActions={
              canMarkIncorrectResponse(message) ? (
                <Popover
                  opened={incorrectPopoverMessageId === message.id}
                  onChange={(opened) => setIncorrectPopoverMessageId(opened ? message.id : null)}
                  position="bottom-end"
                  width={320}
                  shadow="md"
                  withArrow
                >
                  <Popover.Target>
                    <Tooltip
                      label={incorrectMarkedMessageIds.has(message.id) ? 'Ответ уже отмечен' : 'Некорректный ответ'}
                      withArrow
                    >
                      <ActionIcon
                        type="button"
                        variant={incorrectMarkedMessageIds.has(message.id) ? 'light' : 'subtle'}
                        color="orange"
                        size="xs"
                        aria-label="Отметить некорректный ответ"
                        onClick={(event) => {
                          event.stopPropagation()
                          setIncorrectPopoverMessageId((current) => current === message.id ? null : message.id)
                        }}
                      >
                        <IconAlertTriangle size={11} stroke={1.7} />
                      </ActionIcon>
                    </Tooltip>
                  </Popover.Target>
                  <Popover.Dropdown onClick={(event) => event.stopPropagation()}>
                    <Stack gap="xs">
                      <Text size="xs" fw={700}>Некорректный ответ</Text>
                      <Textarea
                        autosize
                        minRows={3}
                        maxRows={6}
                        placeholder="Комментарий проверяющего"
                        value={incorrectCommentByMessage[message.id] || ''}
                        onChange={({ currentTarget: { value } }) => setIncorrectCommentByMessage((prev) => ({
                          ...prev,
                          [message.id]: value,
                        }))}
                      />
                      <Group justify="flex-end" gap={6}>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="gray"
                          onClick={() => setIncorrectPopoverMessageId(null)}
                        >
                          Закрыть
                        </Button>
                        <Button
                          size="compact-xs"
                          color="orange"
                          loading={incorrectSavingMessageId === message.id}
                          onClick={() => saveIncorrectResponse(message)}
                        >
                          Сохранить
                        </Button>
                      </Group>
                    </Stack>
                  </Popover.Dropdown>
                </Popover>
              ) : null
            }
            showTranslation
            translationEnabled
          />
        ))}
      </Stack>
    </ScrollArea>
  )
}

function QualityReviewForm({
  criteria,
  item,
  onSubmit,
  saving,
}: {
  criteria: QualityCriterion[]
  item?: QualityCheckItemRow | null
  onSubmit: (payload: { scores: Record<string, QualityScoreValue>; comment: string; recommendation: string; status: 'reviewed' | 'skipped' }) => void
  saving: boolean
}) {
  const enabledCriteria = criteria.filter((c) => c.enabled)
  const [scores, setScores] = useState<Record<string, QualityScoreValue>>({})
  const [comment, setComment] = useState('')
  const [recommendation, setRecommendation] = useState('')

  useEffect(() => {
    setScores(item?.scores || {})
    setComment(item?.comment || '')
    setRecommendation(item?.recommendation || '')
  }, [item?.id])

  const allFilled = enabledCriteria.every((criterion) => {
    if (!Object.prototype.hasOwnProperty.call(scores, criterion.key)) return false
    const value = scores[criterion.key]
    if (value === 'skip') return criterion.allow_skip !== false
    if (criterion.type === 'weighted_choice') return value === 'yes' || value === 'no' || value === 'na'
    return criterion.type === 'boolean' || Number(value) > 0
  })

  if (!item) return null

  return (
    <Stack className="quality-review-form" gap={0}>
      <Group className="quality-review-head" justify="space-between" gap="xs" wrap="nowrap">
        <Box style={{ minWidth: 0 }}>
          <Text fw={700} truncate>
            #{item.ticket?.ticket_number} · {item.ticket?.subject || 'Без темы'}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            {item.ticket?.project_name || 'Проект'} · {item.ticket?.customer_name || item.ticket?.customer_email || 'Клиент'}
          </Text>
          {item.multiple_operators ? <Text size="xs" c="orange">Участвовали операторы: {(item.operator_participants || []).map((operator) => operator.name).join(', ')} · выбранный оператор: {item.selected_operator?.name || '—'} · его сообщений: {item.selected_operator_message_count || 0} из {item.operator_message_count || 0}</Text> : null}
        </Box>
        {item.weighted_score != null ? (
          <Badge size="lg" color={scoreColor(item.weighted_score)} variant="light">
            {item.weighted_score}%
          </Badge>
        ) : null}
      </Group>
      <Divider />
      <ScrollArea className="quality-review-scroll" type="auto" offsetScrollbars="y">
        <Stack gap="sm" pr="xs">
          <Stack gap={8}>
            {enabledCriteria.map((criterion) => {
              const hasValue = Object.prototype.hasOwnProperty.call(scores, criterion.key)
              const rawValue = scores[criterion.key]
              const val = typeof rawValue === 'number' ? rawValue : 0
              const isSkipped = rawValue === 'skip'
              const criterionStatus = isSkipped
                ? 'Пропущено'
                : criterion.type === 'weighted_choice'
                  ? `${formatCriterionScore(criterion, rawValue)} · Да=${optionWeight(criterion, 'yes')} Нет=${optionWeight(criterion, 'no')} н/д=${optionWeight(criterion, 'na')}`
                  : criterion.type === 'boolean'
                    ? (hasValue ? (val ? 'Да' : 'Нет') : 'не оценено')
                    : SCORE_HINTS[val] || SCORE_HINTS[0]
              return (
                <Paper key={criterion.key} className="quality-criterion-card" withBorder radius="sm" p="xs">
                  <Group className="quality-criterion-row" justify="space-between" align="center" gap="xs" wrap="wrap">
                    <Box className="quality-criterion-text">
                      <Text size="sm" fw={600} lineClamp={2}>
                        {criterion.label}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {criterion.scope === 'ticket' ? 'Весь тикет' : 'Выбранный оператор'} · Вес: {criterion.weight} · {criterionStatus}
                      </Text>
                    </Box>
                    {criterion.type === 'weighted_choice' ? (
                      <Select
                        className="quality-criterion-control"
                        data={[
                          { value: '', label: 'Не оценено' },
                          { value: 'yes', label: `Да (${optionWeight(criterion, 'yes')})` },
                          { value: 'no', label: `Нет (${optionWeight(criterion, 'no')})` },
                          { value: 'na', label: `н/д (${optionWeight(criterion, 'na')})` },
                          ...(criterion.allow_skip === false ? [] : [{ value: 'skip', label: 'Пропущено' }]),
                        ]}
                        value={typeof rawValue === 'string' ? rawValue : ''}
                        onChange={(next) => setScores((prev) => {
                          const updated = { ...prev }
                          if (!next) delete updated[criterion.key]
                          else updated[criterion.key] = next as QualityScoreValue
                          return updated
                        })}
                      />
                    ) : criterion.type === 'boolean' ? (
                      <Select
                        className="quality-criterion-control"
                        data={[
                          { value: '', label: 'Не оценено' },
                          { value: '1', label: 'Да' },
                          { value: '0', label: 'Нет' },
                          ...(criterion.allow_skip === false ? [] : [{ value: 'skip', label: 'Пропущено' }]),
                        ]}
                        value={isSkipped ? 'skip' : hasValue ? String(val ? 1 : 0) : ''}
                        onChange={(next) => setScores((prev) => {
                          const updated = { ...prev }
                          if (!next) delete updated[criterion.key]
                          else updated[criterion.key] = next === 'skip' ? 'skip' : Number(next)
                          return updated
                        })}
                      />
                    ) : (
                      <Group className="quality-criterion-rating" gap="xs" wrap="nowrap">
                        <Rating
                          value={isSkipped ? 0 : val}
                          count={5}
                          onChange={(next) => setScores((prev) => ({ ...prev, [criterion.key]: next }))}
                        />
                        {criterion.allow_skip === false ? null : (
                          <Button
                            size="xs"
                            variant={isSkipped ? 'filled' : 'light'}
                            color="gray"
                            onClick={() => setScores((prev) => ({ ...prev, [criterion.key]: 'skip' }))}
                          >
                            Пропустить
                          </Button>
                        )}
                      </Group>
                    )}
                  </Group>
                </Paper>
              )
            })}
          </Stack>
          <Textarea
            label="Комментарий проверяющего"
            minRows={3}
            value={comment}
            onChange={(event) => setComment(event.currentTarget.value)}
          />
          <Textarea
            label="Рекомендация оператору"
            minRows={2}
            value={recommendation}
            onChange={(event) => setRecommendation(event.currentTarget.value)}
          />
        </Stack>
      </ScrollArea>
      <Group className="quality-review-actions" justify="space-between" gap="xs">
        <Button
          variant="light"
          color="gray"
          loading={saving}
          onClick={() => onSubmit({ scores: {}, comment, recommendation, status: 'skipped' })}
        >
          Пропустить
        </Button>
        <Button
          leftSection={<IconCheck size={16} />}
          loading={saving}
          disabled={!allFilled}
          onClick={() => onSubmit({ scores, comment, recommendation, status: 'reviewed' })}
        >
          Сохранить и дальше
        </Button>
      </Group>
    </Stack>
  )
}

function QualityReportDetailModal({
  session,
  loading,
  criteria,
  exportingMode,
  onDownload,
  onOpenTicket,
  onClose,
}: {
  session: QualitySessionRow | null
  loading: boolean
  criteria: QualityCriterion[]
  exportingMode: 'summary' | 'full' | null
  onDownload: (mode: 'summary' | 'full') => void
  onOpenTicket: (row: QualityIncorrectResponseRow) => void
  onClose: () => void
}) {
  const [openedItem, setOpenedItem] = useState<string | null>(null)
  const [itemDetail, setItemDetail] = useState<QualityCheckItemRow | null>(null)
  const [itemError, setItemError] = useState<string | null>(null)
  useEffect(() => { setOpenedItem(null); setItemDetail(null) }, [session?.id])
  useEffect(() => {
    let stopped = false
    const controller = new AbortController()
    setItemDetail(null)
    setItemError(null)
    if (session?.id && openedItem) {
      void api.qualityItem(session.id, openedItem, controller.signal).then((item) => {
        if (!stopped) setItemDetail(item)
      }).catch((e) => {
        if (!stopped) setItemError(e instanceof ApiError ? e.message : 'Не удалось загрузить детали')
      })
    }
    return () => { stopped = true; controller.abort() }
  }, [session?.id, openedItem])
  const reportCriteria = useMemo(() => {
    const snapshot = Array.isArray(session?.summary?.criteria) ? session.summary.criteria as QualityCriterion[] : null
    if (snapshot?.length) return snapshot
    return criteria
  }, [session?.summary?.criteria, criteria])

  return (
    <Modal
      opened={loading || Boolean(session)}
      onClose={onClose}
      title={session ? (
        <Group justify="space-between" gap="sm" wrap="wrap" style={{ width: '100%' }}>
          <Text fw={700}>Детальный отчет · {session.target_label}</Text>
          <Group gap="xs" wrap="wrap">
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} loading={exportingMode === 'summary'} disabled={Boolean(exportingMode)} onClick={() => onDownload('summary')}>
              Краткий HTML
            </Button>
            <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} loading={exportingMode === 'full'} disabled={Boolean(exportingMode)} onClick={() => onDownload('full')}>
              Полный HTML
            </Button>
          </Group>
        </Group>
      ) : 'Загрузка детального отчета'}
      size="min(1200px, calc(100vw - 24px))"
      centered
      classNames={{ content: 'quality-report-modal-content' }}
    >
      {loading || !session ? (
        <InlineLoader label="Загрузка детального отчета" />
      ) : (
        <Stack gap="sm">
          <Group gap="xs" wrap="wrap">
            <Badge variant="light">Проверяющий: {session.reviewer_name || '—'}</Badge>
            <Badge variant="light">Проекты: {session.project_label || 'Все проекты'}</Badge>
            <Badge variant="light">Период: {session.period_label || '—'}</Badge>
            <Badge variant="light">Обращений: {session.sample_size}</Badge>
            {session.target_type === 'anonymous' ? (
              <Badge color="orange" variant="light">
                Обезличенно · {qualityAnonymousRolesLabel(session.anonymous_role_filters)}
              </Badge>
            ) : null}
            <Badge color={scoreColor(session.summary?.average_score)} variant="light">
              Средний балл: {session.summary?.average_score ?? '—'}%
            </Badge>
            {session.evaluation_mode === 'ai' ? <Badge color="violet" variant="light">AI-проверка · {session.ai_completed || 0}/{session.ai_total || session.sample_size} · ошибок: {session.ai_failed || 0}</Badge> : null}
          </Group>
          <QualitySummaryBlock summary={session.summary} />
          {session.target_type === 'anonymous' && session.summary?.by_operator?.length ? (
            <Paper withBorder p="sm" radius="sm">
              <Text size="sm" fw={700} mb="xs">Итоги по операторам</Text>
              <Table.ScrollContainer minWidth={520}>
                <Table striped withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Оператор</Table.Th>
                      <Table.Th>Проверено тикетов</Table.Th>
                      <Table.Th>Средний балл</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {session.summary.by_operator.map((row) => (
                      <Table.Tr key={row.operator_id}>
                        <Table.Td>{row.operator_name}</Table.Td>
                        <Table.Td>{row.tickets_reviewed}</Table.Td>
                        <Table.Td>
                          <Badge color={scoreColor(row.average_score)} variant="light">
                            {row.average_score ?? '—'}%
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Paper>
          ) : null}
          {session.items?.some((item) => item.incorrect_responses?.length) ? (
            <Paper withBorder p="sm" radius="sm" bg="var(--mantine-color-orange-light)">
              <Group justify="space-between" gap="xs" wrap="wrap" mb="xs">
                <Text size="sm" fw={700}>Некорректные ответы</Text>
                <Badge color="orange" variant="light">
                  {session.items.reduce((sum, item) => sum + (item.incorrect_responses?.length || 0), 0)}
                </Badge>
              </Group>
              <Stack gap="xs">
                {session.items.flatMap((item) => item.incorrect_responses || []).map((mark) => (
                  <Paper key={mark.id} withBorder radius="sm" p="xs" bg="var(--mantine-color-body)">
                    <Group justify="space-between" gap="xs" wrap="nowrap" align="flex-start">
                      <Box style={{ minWidth: 0 }}>
                        <Text size="xs" c="dimmed">
                          #{mark.ticket_number ?? mark.item_position ?? '—'} · {mark.operator_name || 'Оператор'} · {mark.created_at ? formatDateTimeDot(mark.created_at) : '—'}
                        </Text>
                        <Text size="sm" lineClamp={2} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {mark.message_content || '—'}
                        </Text>
                        <Text size="sm" c="orange" mt={3} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {mark.comment || '—'}
                        </Text>
                      </Box>
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        aria-label="Перейти в тикет"
                        onClick={() => onOpenTicket(mark)}
                        style={{ flexShrink: 0 }}
                      >
                        <IconExternalLink size={16} stroke={1.5} />
                      </ActionIcon>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          ) : null}
          {session.evaluation_mode === 'ai' ? (
            <Paper withBorder p="sm" radius="sm">
              <Text size="sm" fw={700}>Промт и параметры проверки</Text>
              <Text size="xs" mt={4} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 180, overflow: 'auto' }}>{session.ai_prompt || '—'}</Text>
              <Text size="xs" c="dimmed" mt={6}>Токены vLLM не показываются в отчёте. Для каждого обращения ниже сохранены фактический запрос и ответ модели.</Text>
            </Paper>
          ) : null}
          {session.evaluation_mode === 'ai' && session.summary?.ai_summary ? (
            <Accordion variant="contained" radius="sm">
              <Accordion.Item value="aggregate-ai-summary">
                <Accordion.Control>
                  <Group justify="space-between" gap="xs" wrap="wrap">
                    <Text size="sm" fw={700}>Запрос общего заключения</Text>
                    <Badge color={session.summary.ai_summary.status === 'completed' ? 'green' : 'orange'} variant="light">
                      {session.summary.ai_summary.status === 'completed' ? 'Получено' : 'Ошибка'}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {session.summary.ai_summary.status === 'completed' ? (
                    <Stack gap="sm">
                      <Box><Text size="xs" c="dimmed">Заключение модели</Text><Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{session.summary.ai_summary.conclusion || '—'}</Text></Box>
                      {session.summary.ai_summary.strengths?.length ? <Box><Text size="xs" c="dimmed">Сильные стороны</Text><Text size="sm">{session.summary.ai_summary.strengths.join(' · ')}</Text></Box> : null}
                      {session.summary.ai_summary.recommendations?.length ? <Box><Text size="xs" c="dimmed">Рекомендации</Text><Text size="sm">{session.summary.ai_summary.recommendations.join(' · ')}</Text></Box> : null}
                    </Stack>
                  ) : <Alert color="orange">{session.summary.ai_summary.error || 'AI не сформировал общее заключение. Результаты отдельных проверок сохранены.'}</Alert>}
                  <Accordion variant="separated" radius="sm" mt="sm">
                    <Accordion.Item value="aggregate-ai-technical">
                      <Accordion.Control><Text size="xs">Технические данные запроса и ответа</Text></Accordion.Control>
                      <Accordion.Panel>
                        <Text size="xs" c="dimmed">Промт</Text><Text size="xs" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 180, overflow: 'auto' }}>{session.summary.ai_summary.prompt || '—'}</Text>
                        <Text size="xs" c="dimmed" mt="sm">Результаты, переданные модели</Text><Text size="xs" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 220, overflow: 'auto' }}>{session.summary.ai_summary.request || '—'}</Text>
                        {session.summary.ai_summary.response ? <><Text size="xs" c="dimmed" mt="sm">Ответ модели</Text><Text size="xs" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 220, overflow: 'auto' }}>{session.summary.ai_summary.response}</Text></> : null}
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          ) : null}
          <ScrollArea h="calc(100vh - 300px)" type="auto" offsetScrollbars="y">
            <Accordion value={openedItem} onChange={setOpenedItem} variant="separated" radius="sm" pr="xs">
              {(session.items ?? []).map((stub) => {
                const item = itemDetail?.id === stub.id ? { ...itemDetail, ...stub, ticket: itemDetail.ticket } : stub
                const notApplicable = Array.isArray(item.ai_response_json?.not_applicable)
                  ? item.ai_response_json.not_applicable.map(String)
                  : []
                return (
                  <Accordion.Item key={item.id} className="quality-report-item" value={item.id}>
                    <Accordion.Control>
                      <Group justify="space-between" align="center" gap="sm" wrap="wrap" pr="xs">
                        <Box style={{ minWidth: 0, flex: '1 1 280px' }}>
                          <Text fw={700} truncate>Обращение #{item.ticket?.ticket_number || item.position}</Text>
                          <Text size="sm" c="dimmed" truncate>{item.ticket?.subject || 'Без темы'}</Text>
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            Результат: {item.weighted_score != null ? `${item.weighted_score}%` : item.comment || 'не оценено'}
                          </Text>
                        </Box>
                        <Group gap="xs" wrap="wrap">
                          <Text size="xs" c="dimmed">{item.ticket?.created_at ? formatDateTimeDot(item.ticket.created_at) : '—'}</Text>
                          <Badge color={item.status === 'reviewed' ? 'green' : item.status === 'skipped' ? 'gray' : 'blue'} variant="light">{item.status === 'reviewed' ? 'Оценено' : item.status === 'skipped' ? 'Пропущено' : 'Не оценено'}</Badge>
                          <Badge color={scoreColor(item.weighted_score)} variant="light">Итог: {item.weighted_score != null ? `${item.weighted_score}%` : '—'}</Badge>
                          {item.multiple_operators ? <Badge color="orange" variant="light">Несколько операторов</Badge> : null}
                        </Group>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      {itemError ? <Alert color="red">{itemError}</Alert> : itemDetail?.id !== item.id ? <Text>Загрузка деталей…</Text> : null}
                      <Paper withBorder p="sm" radius="sm">
                        <Text size="xs" c="dimmed">Создано</Text>
                        <Text size="sm">{item.ticket?.created_at ? formatDateTimeDot(item.ticket.created_at) : '—'}</Text>
                        <Text size="xs" c="dimmed" mt="sm">Клиент</Text>
                        <Text size="sm">{item.ticket?.customer_name || item.ticket?.customer_email || '—'}</Text>
                        {item.operator_participants?.length ? (
                          <>
                            <Text size="xs" c="dimmed" mt="sm">Операторы в обращении</Text>
                            <Text size="sm">{item.operator_participants.map((operator) => operator.name).join(', ')}</Text>
                            {item.selected_operator ? <Text size="xs" c="blue">В оценку ответов: {item.selected_operator.name}</Text> : null}
                          </>
                        ) : null}
                      </Paper>
                      {item.incorrect_responses?.length ? (
                        <Paper withBorder p="xs" radius="sm" mt="sm" bg="var(--mantine-color-orange-light)">
                          <Text size="xs" fw={700} mb={6}>Некорректные ответы</Text>
                          <Stack gap={6}>
                            {item.incorrect_responses.map((mark) => (
                              <Box key={mark.id}>
                                <Text size="xs" c="dimmed">
                                  {mark.operator_name || 'Оператор'} · {mark.created_at ? formatDateTimeDot(mark.created_at) : '—'}
                                </Text>
                                <Text size="sm" lineClamp={2} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{mark.message_content || '—'}</Text>
                                <Text size="sm" c="orange" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{mark.comment || '—'}</Text>
                              </Box>
                            ))}
                          </Stack>
                        </Paper>
                      ) : null}
                      {session.evaluation_mode === 'ai' ? (
                        <Box mt="sm">
                          <Paper withBorder p="sm" radius="sm">
                            <Text size="sm" fw={700} mb={4}>Результат проверки</Text>
                            <AiResponseView item={item} criteria={reportCriteria} />
                          </Paper>
                          <Accordion variant="contained" radius="sm" mt="sm">
                            <Accordion.Item value={`report-request-${item.id}`}>
                              <Accordion.Control>
                                <Box>
                                  <Text size="sm" fw={700}>Что отправлено модели</Text>
                                  <Text size="xs" c="dimmed">Открыть запрос и части диалога</Text>
                                </Box>
                              </Accordion.Control>
                              <Accordion.Panel>
                                <AiRequestView request={item.ai_request} />
                              </Accordion.Panel>
                            </Accordion.Item>
                          </Accordion>
                        </Box>
                      ) : (
                        <Box className="quality-report-item-grid" mt="sm">
                          <Paper withBorder p="xs" radius="sm">
                            <Text size="xs" fw={700}>Оценки по критериям</Text>
                            <Box mt={4}>
                              <CriteriaScoresList
                                criteria={reportCriteria}
                                scores={item.scores || {}}
                                notApplicable={notApplicable}
                              />
                            </Box>
                          </Paper>
                          <Paper withBorder p="xs" radius="sm">
                            <Text size="xs" c="dimmed">Комментарий модели/проверяющего</Text>
                            <Text size="sm" mt={3} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.comment || '—'}</Text>
                            <Text size="xs" c="dimmed" mt="sm">Рекомендация</Text>
                            <Text size="sm" mt={3} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.recommendation || '—'}</Text>
                          </Paper>
                        </Box>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                )
              })}
            </Accordion>
          </ScrollArea>
        </Stack>
      )}
    </Modal>
  )
}

export function QualityArmPage() {
  const { user } = useAuth()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])
  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role)
  const canAccess = hasPermission(effectivePerm, 'access_quality_arm')

  const [tab, setTab] = useState<string | null>('check')
  const [settingsTab, setSettingsTab] = useState<string | null>('general')
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [targets, setTargets] = useState<QualityTargetRow[]>([])
  const [settings, setSettings] = useState<QualitySettingsResponse | null>(null)
  const [aiSettings, setAiSettings] = useState<QualityAISettings | null>(null)
  const [session, setSession] = useState<QualitySessionRow | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [history, setHistory] = useState<QualitySessionRow[]>([])
  const [reports, setReports] = useState<QualityReportResponse | null>(null)
  const [reportDays, setReportDays] = useState(30)
  const [incorrectResponses, setIncorrectResponses] = useState<QualityIncorrectResponsesResponse | null>(null)
  const [incorrectResponsesLoading, setIncorrectResponsesLoading] = useState(false)
  const [incorrectResponsesExporting, setIncorrectResponsesExporting] = useState(false)
  const [reportDetail, setReportDetail] = useState<QualitySessionRow | null>(null)
  const [reportDetailLoading, setReportDetailLoading] = useState(false)
  const [reportExportingMode, setReportExportingMode] = useState<'summary' | 'full' | null>(null)
  const [mobilePane, setMobilePane] = useState('conversation')
  const [startConfirmation, setStartConfirmation] = useState(false)
  const [startPreview, setStartPreview] = useState<QualitySessionPreview | null>(null)
  const [pendingStart, setPendingStart] = useState<QualitySessionCreatePayload | null>(null)
  const [startError, setStartError] = useState<string | null>(null)
  const [previewChanged, setPreviewChanged] = useState(false)
  const [standardPreview, setStandardPreview] = useState<QualityStandardPreviewTarget | null>(null)

  const [targetValue, setTargetValue] = useState('ai:')
  const [anonymousRoleFilters, setAnonymousRoleFilters] = useState<string[]>(['operator'])
  const [evaluationMode, setEvaluationMode] = useState<'manual' | 'ai'>('manual')
  const [aiBatchSize, setAiBatchSize] = useState(3)
  const [aiProjectInstructions, setAiProjectInstructions] = useState<Record<string, string>>({})
  const [gpuModels, setGpuModels] = useState<VLLMGatewayModel[]>([])
  const [loadingGpuModels, setLoadingGpuModels] = useState(false)
  const [projectIds, setProjectIds] = useState<string[]>([])
  const [samplePercent, setSamplePercent] = useState(10)
  const [sampleMode, setSampleMode] = useState<'percent' | 'count'>('percent')
  const [sampleCount, setSampleCount] = useState(10)
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'previous_month' | 'custom'>('week')
  const [periodStartDate, setPeriodStartDate] = useState('')
  const [periodEndDate, setPeriodEndDate] = useState('')

  const criteria = useMemo(() => {
    const snapshot = Array.isArray(session?.summary?.criteria) ? session.summary.criteria as QualityCriterion[] : null
    if (snapshot?.length) return snapshot
    const targetType = targetValue.startsWith('ai:') ? 'ai' : 'operator'
    if (targetType === 'ai') return settings?.criteria_ai ?? []
    return settings?.criteria ?? []
  }, [session?.summary?.criteria, settings?.criteria, settings?.criteria_ai, targetValue])
  const standardPreviewData = useMemo(() => {
    if (!settings || !standardPreview) return null
    const current = standardPreview === 'bot' ? settings.criteria_ai || [] : settings.criteria || []
    const standard = standardPreview === 'bot' ? settings.default_criteria_ai || [] : settings.default_criteria || []
    const currentByKey = new Map(current.map((item) => [item.key, item]))
    const standardByKey = new Map(standard.map((item) => [item.key, item]))
    const keys = Array.from(new Set([...current.map((item) => item.key), ...standard.map((item) => item.key)]))
    return {
      title: standardPreview === 'bot' ? 'Стандарт критериев Мозгобота' : 'Стандарт критериев операторов',
      current,
      standard,
      rows: keys.map((key) => {
        const currentItem = currentByKey.get(key)
        const standardItem = standardByKey.get(key)
        return {
          key,
          current: currentItem,
          standard: standardItem,
          status: criterionCompareStatus(currentItem, standardItem),
        }
      }),
    }
  }, [settings, standardPreview])
  const items = session?.items ?? []
  const selectedItem = items[activeIndex] ?? null
  const [activeDetail, setActiveDetail] = useState<QualityCheckItemRow | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [detailRefresh, setDetailRefresh] = useState(0)
  const activeItem = selectedItem && activeDetail?.id === selectedItem.id
    ? { ...activeDetail, ...selectedItem, ticket: activeDetail.ticket } : null

  useEffect(() => {
    let stopped = false
    const controller = new AbortController()
    setActiveDetail(null)
    setDetailError(null)
    if (session?.id && selectedItem?.id) {
      void api.qualityItem(session.id, selectedItem.id, controller.signal).then((detail) => {
        if (!stopped) setActiveDetail(detail)
      }).catch((e) => {
        if (!stopped) setDetailError(e instanceof ApiError ? e.message : 'Не удалось загрузить обращение проверки')
      })
    }
    return () => { stopped = true; controller.abort() }
  }, [session?.id, selectedItem?.id, selectedItem?.ai_status, selectedItem?.ai_completed_at, selectedItem?.reviewed_at, detailRefresh])
  const reviewedCount = items.filter((item) => item.status === 'reviewed').length
  const skippedCount = items.filter((item) => item.status === 'skipped').length
  const doneCount = reviewedCount + skippedCount
  const progress = items.length ? Math.round((doneCount / items.length) * 100) : 0
  const selectedTargetType = targetValue.split(':')[0]
  const anonymousModeSelected = selectedTargetType === 'anonymous'

  const { openOrSelectTab } = useWorkspaceTabs()

  const loadBase = useCallback(async () => {
    if (!canAccess) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [projectsRes, targetsRes, settingsRes, aiSettingsRes, historyRes, activeRes] = await Promise.all([
        api.projects(),
        api.qualityTargets(),
        api.qualitySettings(),
        api.qualityAISettings().catch(() => null),
        api.qualityHistory({ limit: 20, offset: 0 }).catch(() => ({ items: [], total: 0, limit: 20, offset: 0 })),
        api.qualityActiveSession().catch(() => null),
      ])
      setProjects(projectsRes)
      setTargets(targetsRes.items ?? [])
      setSettings(settingsRes)
      setAiSettings(aiSettingsRes)
      if (aiSettingsRes?.batch_size) setAiBatchSize(aiSettingsRes.batch_size)
      setSamplePercent(settingsRes.default_sample_percent || 10)
      setSampleCount(settingsRes.default_max_tickets || 10)
      setHistory(historyRes.items ?? [])
      if (activeRes) {
        setSession(activeRes)
        setEvaluationMode(activeRes.evaluation_mode === 'ai' ? 'ai' : 'manual')
        setAiBatchSize(activeRes.ai_batch_size || 3)
        setActiveIndex(Math.max(0, (activeRes.items ?? []).findIndex((item) => item.status === 'pending')))
        setPeriod((activeRes.period as typeof period) || 'week')
        setPeriodStartDate(activeRes.period_start_date || '')
        setPeriodEndDate(activeRes.period_end_date || '')
        setSampleMode(activeRes.sample_mode === 'count' ? 'count' : 'percent')
        setSampleCount(activeRes.sample_count || settingsRes.default_max_tickets || 10)
        setSamplePercent(activeRes.sample_percent || settingsRes.default_sample_percent || 10)
        if (activeRes.target_type === 'anonymous') {
          setTargetValue('anonymous:')
          setEvaluationMode('manual')
          setAnonymousRoleFilters(activeRes.anonymous_role_filters?.length ? activeRes.anonymous_role_filters : ['operator'])
        }
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить АРМ Качества')
    } finally {
      setLoading(false)
    }
  }, [canAccess])

  useEffect(() => {
    const summaryStatus = session?.summary?.ai_summary_status
    const shouldPoll = Boolean(session && ((session.evaluation_mode === 'ai' && session.status === 'in_progress') || summaryStatus === 'queued' || summaryStatus === 'running'))
    if (!session || !shouldPoll) return
    let stopped = false
    const controller = new AbortController()
    const poll = async () => {
      try {
        const fresh = await api.qualityProgress(session.id, controller.signal)
        if (!stopped) {
          setSession((current) => {
            if (!current || current.id !== fresh.id) return current
            const updates = new Map(fresh.items.map((item) => [item.id, item]))
            return { ...current, ...fresh, items: (current.items || []).map((item) => ({ ...item, ...updates.get(item.id) })) }
          })
          if (fresh.status !== 'in_progress') {
            const [h, r] = await Promise.all([api.qualityHistory({ limit: 20, offset: 0 }), api.qualityReports({ days: reportDays })])
            if (!stopped) {
              setHistory(h.items ?? [])
              setReports(r)
            }
          }
        }
      } catch {
        // Retry only after this request has finished.
      }
    }
    const stopPolling = startQualityPolling(poll)
    return () => {
      stopped = true
      stopPolling()
      controller.abort()
    }
  }, [session?.id, session?.evaluation_mode, session?.status, session?.summary?.ai_summary_status, reportDays])

  useEffect(() => {
    void loadBase()
  }, [loadBase])

  const loadIncorrectResponses = useCallback(async () => {
    setIncorrectResponsesLoading(true)
    try {
      setIncorrectResponses(await api.qualityIncorrectResponses({ days: reportDays, limit: 200, offset: 0 }))
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить некорректные ответы',
        color: 'red',
      })
    } finally {
      setIncorrectResponsesLoading(false)
    }
  }, [reportDays])

  useEffect(() => {
    if (tab === 'incorrect') void loadIncorrectResponses()
  }, [loadIncorrectResponses, tab])

  const onIncorrectResponseCreated = useCallback((itemId: string, mark: QualityIncorrectResponseRow) => {
    setActiveDetail((current) => current?.id === itemId ? {
      ...current, incorrect_responses: [...(current.incorrect_responses || []), mark],
    } : current)
    setIncorrectResponses((current) => current ? {
      ...current,
      total: current.total + 1,
      items: [mark, ...current.items].slice(0, current.limit || 200),
    } : current)
  }, [])

  const downloadIncorrectResponses = useCallback(async () => {
    setIncorrectResponsesExporting(true)
    try {
      const blob = await api.qualityIncorrectResponsesHtml({ days: reportDays })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `quality-incorrect-responses-${reportDays}d.html`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      showNotification({ title: 'Выгрузка готова', message: 'HTML-файл с некорректными ответами сохранен.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка выгрузки',
        message: e instanceof ApiError ? e.message : 'Не удалось выгрузить список',
        color: 'red',
      })
    } finally {
      setIncorrectResponsesExporting(false)
    }
  }, [reportDays])

  const openTicketFromIncorrectResponse = useCallback((row: QualityIncorrectResponseRow) => {
    const ref = row.ticket_ref || row.ticket_id
    if (!ref) return
    openOrSelectTab(`/tickets/${encodeURIComponent(ref)}`)
  }, [openOrSelectTab])

  const targetOptions = useMemo(
    () => [
      {
        value: 'anonymous:',
        label: 'Обезличенная проверка',
      },
      ...targets.map((target) => ({
        value: `${target.target_type}:${target.id || ''}`,
        label: target.label,
      })),
    ],
    [targets],
  )
  const projectOptions = useMemo(
    () => projects.map((project) => ({ value: project.id, label: project.name })),
    [projects],
  )

  const startSession = useCallback(async () => {
    const [targetType, targetId] = targetValue.split(':')
    const isAnonymousMode = targetType === 'anonymous'
    if (isAnonymousMode && !anonymousRoleFilters.length) {
      showNotification({ title: 'Выберите роли', message: 'Для обезличенной проверки нужна хотя бы одна роль.', color: 'orange' })
      return
    }
    if (period === 'custom') {
      if (!periodStartDate || !periodEndDate) {
        showNotification({ title: 'Укажите период', message: 'Для собственного периода заполните обе даты.', color: 'orange' })
        return
      }
      if (periodStartDate > periodEndDate) {
        showNotification({ title: 'Проверьте даты', message: 'Дата начала не может быть позже даты окончания.', color: 'orange' })
        return
      }
    }
    setBusy(true)
    setError(null)
    try {
      const payload: QualitySessionCreatePayload = {
        target_type: targetType === 'ai' ? 'ai' : isAnonymousMode ? 'anonymous' : 'operator',
        target_user_id: targetType === 'operator' ? targetId : null,
        anonymous_role_filters: isAnonymousMode ? [...anonymousRoleFilters] : undefined,
        project_ids: [...projectIds],
        period,
        period_start_date: period === 'custom' ? periodStartDate : undefined,
        period_end_date: period === 'custom' ? periodEndDate : undefined,
        sample_mode: sampleMode,
        sample_percent: samplePercent,
        sample_count: sampleMode === 'count' ? sampleCount : undefined,
        evaluation_mode: isAnonymousMode ? 'manual' : evaluationMode,
        ai_batch_size: aiBatchSize,
        ai_project_instructions: evaluationMode === 'ai' ? { ...aiProjectInstructions } : undefined,
      }
      setPendingStart(payload)
      setStartPreview(null)
      setStartError(null)
      setPreviewChanged(false)
      setStartConfirmation(true)
      setStartPreview(await api.qualitySessionPreview(payload))
    } catch (e) {
      setStartError(e instanceof ApiError ? e.message : 'Не удалось рассчитать выборку')
    } finally {
      setBusy(false)
    }
  }, [targetValue, anonymousRoleFilters, projectIds, period, periodStartDate, periodEndDate, sampleMode, samplePercent, sampleCount, evaluationMode, aiBatchSize, aiProjectInstructions])

  const confirmStartSession = async () => {
    if (!pendingStart || !startPreview?.can_start || busy) return
    setBusy(true)
    setStartError(null)
    try {
      const res = await api.qualitySessionCreate({
        ...pendingStart,
        expected_total_candidates: startPreview.total_candidates,
        expected_sample_size: startPreview.sample_size,
      })
      setSession(res)
      setMobilePane('conversation')
      setActiveIndex(0)
      setStartConfirmation(false)
      showNotification({ title: res.evaluation_mode === 'ai' ? 'AI-проверка запущена' : 'Проверка создана', message: `Выбрано обращений: ${res.sample_size}`, color: 'green' })
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && (e.detail as { code?: string } | undefined)?.code === 'quality_preview_changed') {
        setStartPreview(null)
        setPreviewChanged(true)
        try {
          setStartPreview(await api.qualitySessionPreview(pendingStart))
        } catch (refreshError) {
          setStartError(refreshError instanceof Error ? refreshError.message : 'Не удалось обновить расчет')
        }
      } else {
        setStartError(e instanceof Error ? e.message : 'Не удалось создать проверку')
      }
    } finally {
      setBusy(false)
    }
  }

  const submitScore = useCallback(
    async (payload: { scores: Record<string, QualityScoreValue>; comment: string; recommendation: string; status: 'reviewed' | 'skipped' }) => {
      if (!session || !activeItem) return
      setBusy(true)
      try {
        const res = await api.qualityScoreItem(session.id, activeItem.id, payload)
        const updatedItems = (session.items ?? []).map((item) =>
          item.id === activeItem.id
            ? {
                ...item,
                status: res.item.status,
                ai_status: res.item.ai_status,
                weighted_score: res.item.weighted_score,
                reviewed_at: res.item.reviewed_at,

              }
            : item,
        )
        setSession({
          ...session,
          items: updatedItems,
          summary: res.session_summary ?? session.summary,
          status: res.session_status ?? session.status,
        })
        const nextPendingIndex = updatedItems.findIndex(
          (item, index) => index > activeIndex && item.status === 'pending',
        )
        const nextIndex =
          nextPendingIndex >= 0
            ? nextPendingIndex
            : Math.min(activeIndex + 1, Math.max(0, updatedItems.length - 1))
        setActiveIndex(nextIndex)
        setMobilePane('conversation')
      } catch (e) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось сохранить оценку',
          color: 'red',
        })
      } finally {
        setBusy(false)
      }
    },
    [session, activeItem, activeIndex],
  )

  const completeSession = useCallback(async () => {
    if (!session) return
    setBusy(true)
    try {
      await api.qualityComplete(session.id)
      const [h, r] = await Promise.all([
        api.qualityHistory({ limit: 20, offset: 0 }),
        api.qualityReports({ days: reportDays }),
      ])
      setSession(null)
      setActiveIndex(0)
      setHistory(h.items ?? [])
      setReports(r)
      showNotification({ title: 'Проверка завершена', message: 'Результаты добавлены в отчеты.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось завершить проверку',
        color: 'red',
      })
    } finally {
      setBusy(false)
    }
  }, [session, reportDays])

  const cancelSession = useCallback(async () => {
    if (!session) return
    setBusy(true)
    try {
      setSession(await api.qualityCancel(session.id))
      showNotification({ title: 'Остановлено', message: 'Фоновая проверка остановлена. Уже обработанные результаты сохранены.', color: 'orange' })
    } catch (e) {
      showNotification({ title: 'Ошибка', message: e instanceof ApiError ? e.message : 'Не удалось остановить проверку', color: 'red' })
    } finally {
      setBusy(false)
    }
  }, [session])

  const resumeSession = useCallback(async (sessionId?: string) => {
    const id = sessionId || session?.id
    if (!id) return
    setBusy(true)
    try {
      const res = await api.qualityResume(id)
      setSession(res)
      setTab('check')
      try {
        const h = await api.qualityHistory({ limit: 20, offset: 0 })
        setHistory(h.items ?? [])
      } catch {
        /* history refresh best-effort */
      }
      showNotification({
        title: 'Возобновлено',
        message: res.evaluation_mode === 'ai'
          ? 'AI-проверка продолжится с того места, где остановилась.'
          : 'Проверка продолжится с того места, где остановилась.',
        color: 'green',
      })
    } catch (e) {
      showNotification({ title: 'Ошибка', message: e instanceof ApiError ? e.message : 'Не удалось возобновить проверку', color: 'red' })
    } finally {
      setBusy(false)
    }
  }, [session])

  const isResumable = useCallback((row: QualitySessionRow): boolean => {
    if (row.status !== 'canceled') return false
    if (row.can_resume != null) return row.can_resume
    const items = row.items || []
    if (!items.length) return false
    if (row.evaluation_mode === 'ai') {
      return items.some((it) => it.ai_status !== 'completed' && it.ai_status !== 'failed')
    }
    return items.some((it) => it.status !== 'reviewed' && it.status !== 'skipped')
  }, [])

  const saveSettings = useCallback(async () => {
    if (!settings) return
    setBusy(true)
    try {
      const res = await api.qualitySettingsUpdate(settings)
      setSettings(res)
      showNotification({ title: 'Сохранено', message: 'Настройки качества обновлены.', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить настройки',
        color: 'red',
      })
    } finally {
      setBusy(false)
    }
  }, [settings])

  const resetCriteriaToDefaults = useCallback((target: 'operators' | 'bot') => {
    const label = target === 'bot' ? 'Мозгобота' : 'операторов'
    openConfirmModal({
      title: `Вернуть критерии ${label} к стандарту?`,
      children: (
        <Text size="sm">
          Текущий список критериев будет заменён стандартными значениями. Уже созданные проверки и отчёты не изменятся.
        </Text>
      ),
      labels: { confirm: 'Вернуть', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setBusy(true)
        try {
          const res = await api.qualityCriteriaReset(target)
          setSettings(res)
          showNotification({
            title: 'Критерии восстановлены',
            message: `Список критериев ${label} возвращён к стандартным значениям.`,
            color: 'green',
          })
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось восстановить критерии',
            color: 'red',
          })
        } finally {
          setBusy(false)
        }
      },
    })
  }, [])

  const saveCriteriaAsDefaults = useCallback(async (target: 'operators' | 'bot') => {
    if (!settings) return
    const criteriaToSave = target === 'bot' ? settings.criteria_ai : settings.criteria
    const label = target === 'bot' ? 'Мозгобота' : 'операторов'
    setBusy(true)
    try {
      const res = await api.qualityCriteriaDefaultsUpdate(target, criteriaToSave || [])
      setSettings(res)
      showNotification({
        title: 'Стандарт обновлён',
        message: `Текущий список критериев ${label} сохранён как стандартный.`,
        color: 'green',
      })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить стандарт критериев',
        color: 'red',
      })
    } finally {
      setBusy(false)
    }
  }, [settings])

  const saveAiSettings = useCallback(async () => {
    if (!aiSettings) return
    setBusy(true)
    try {
      const saved = await api.qualityAISettingsUpdate(aiSettings)
      setAiSettings(saved)
      showNotification({
        title: 'Сохранено',
        message: saved.assistant_sync === 'failed' ? `Настройки сохранены, но Assistant не синхронизирован: ${saved.assistant_error || 'ошибка API'}` : saved.assistant_sync === 'recreated' ? 'Настройки сохранены, Assistant пересоздан с актуальными критериями.' : saved.assistant_sync === 'updated' ? 'Настройки сохранены, Assistant обновлен с учетом критериев.' : 'Общие AI-настройки АРМ качества обновлены.',
        color: saved.assistant_sync === 'failed' ? 'orange' : 'green',
      })
    } catch (e) {
      showNotification({ title: 'Ошибка', message: e instanceof ApiError ? e.message : 'Не удалось сохранить AI-настройки', color: 'red' })
    } finally {
      setBusy(false)
    }
  }, [aiSettings])

  const loadGpuModels = useCallback(async () => {
    setLoadingGpuModels(true)
    try {
      const result = await api.qualityAIModels()
      setGpuModels(result.models ?? [])
      if (result.models?.length === 1 && aiSettings && !aiSettings.model) {
        setAiSettings({ ...aiSettings, model: result.models[0].id })
      }
      showNotification({ title: 'Модели подгружены', message: `Доступно на GPU: ${result.models?.length ?? 0}`, color: 'green' })
    } catch (e) {
      showNotification({ title: 'Ошибка', message: e instanceof ApiError ? e.message : 'Не удалось подгрузить модели с GPU', color: 'red' })
    } finally {
      setLoadingGpuModels(false)
    }
  }, [aiSettings])

  const loadReports = useCallback(async () => {
    setBusy(true)
    try {
      setReports(await api.qualityReports({ days: reportDays }))
    } catch (e) {
      showNotification({ title: 'Ошибка', message: e instanceof ApiError ? e.message : 'Не удалось загрузить отчёты', color: 'red' })
    } finally {
      setBusy(false)
    }
  }, [reportDays])

  useEffect(() => {
    if (!canAccess || tab !== 'reports') return
    const timer = window.setTimeout(() => void loadReports(), 250)
    return () => window.clearTimeout(timer)
  }, [canAccess, tab, loadReports])

  const openReportDetail = useCallback(async (sessionId: string) => {
    setReportDetailLoading(true)
    setReportDetail(null)
    try {
      setReportDetail(await api.qualitySession(sessionId))
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить детальный отчет',
        color: 'red',
      })
    } finally {
      setReportDetailLoading(false)
    }
  }, [])

  const closeReportDetail = useCallback(() => {
    setReportDetail(null)
    setReportDetailLoading(false)
  }, [])

  const downloadReport = useCallback(async (mode: 'summary' | 'full') => {
    if (!reportDetail) return
    setReportExportingMode(mode)
    try {
      const blob = await api.qualityReportHtml(reportDetail.id, mode)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `quality-report-${reportDetail.id}-${mode}.html`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      showNotification({ title: 'Отчет выгружен', message: 'HTML-файл сохранен. Его можно открыть в браузере или распечатать в PDF.', color: 'green' })
    } catch (e) {
      showNotification({ title: 'Ошибка выгрузки', message: e instanceof ApiError ? e.message : 'Не удалось выгрузить отчет', color: 'red' })
    } finally {
      setReportExportingMode(null)
    }
  }, [reportDetail])

  const activeSession = session?.status === 'in_progress'

  const focusedReview = activeSession && tab === 'check'
  const sectionLinks = [
    { value: 'check', label: 'Проверка', icon: IconPlayerPlay },
    { value: 'reports', label: 'Отчеты', icon: IconChartBar },
    { value: 'incorrect', label: 'Некорректные ответы', icon: IconAlertTriangle },
    { value: 'history', label: 'История', icon: IconHistory },
    { value: 'settings', label: 'Настройки', icon: IconSettings },
  ]
  const sectionMenu = (
    <Menu position="bottom-start" withinPortal>
      <Menu.Target>
        <ActionIcon variant="light" aria-label="Меню АРМ качества" title="Меню АРМ качества">
          <IconMenu2 size={18} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>АРМ качества</Menu.Label>
        {sectionLinks.map(({ value, label, icon: Icon }) => (
          <Menu.Item key={value} leftSection={<Icon size={16} />} onClick={() => setTab(value)}>
            {label}
          </Menu.Item>
        ))}
        <Menu.Divider />
        <Menu.Label>Настройки</Menu.Label>
        {[['general', 'Общие параметры'], ['operators', 'Критерии операторов'], ['bot', 'Критерии Мозгобота'], ['ai', 'AI-проверка']].map(([value, label]) => (
          <Menu.Item key={value} leftSection={<IconSettings size={16} />} onClick={() => { setSettingsTab(value); setTab('settings') }}>{label}</Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )

  if (!canAccess) {
    return (
      <PageShell>
        <Alert color="red" title="Нет доступа">
          У вашей роли нет доступа к АРМ Качества.
        </Alert>
      </PageShell>
    )
  }

  if (loading) {
    return (
      <PageShell>
        <PageCenterLoader label="Загрузка АРМ Качества" minHeight={320} />
      </PageShell>
    )
  }

  return (
    <PageShell variant="fill">
      <Stack gap="sm" className={`quality-arm-page${focusedReview ? ' quality-arm-page--focused' : ''}`} style={{ flex: 1, minHeight: 0 }}>
        {!focusedReview ? (
          <Group className="quality-page-heading" justify="space-between" gap="xs">
            <Group gap="xs">
              {sectionMenu}
              <Text size="sm" fw={700}>{sectionLinks.find((section) => section.value === tab)?.label || 'Проверка'}{tab === 'settings' ? ` · ${{ general: 'Общие', operators: 'Операторы', bot: 'Мозгобот', ai: 'AI' }[settingsTab || 'general'] || ''}` : ''}</Text>
              {session ? <Badge variant="light" color={scoreColor(session.summary?.average_score)}>{session.summary?.average_score ?? '—'}%</Badge> : null}
            </Group>
            <Group gap="xs">
              {activeSession ? <Button size="compact-sm" leftSection={<IconPlayerPlay size={14} />} onClick={() => setTab('check')}>Вернуться к проверке · {doneCount}/{items.length}</Button> : null}
              <ActionIcon variant="subtle" aria-label="Обновить АРМ качества" title="Обновить" onClick={() => void loadBase()}><IconRefresh size={16} /></ActionIcon>
            </Group>
          </Group>
        ) : null}
        {error ? <Alert color="red">{error}</Alert> : null}
        {detailError ? <Alert color="red">{detailError}<Button size="xs" variant="subtle" onClick={() => setDetailRefresh((value) => value + 1)}>Повторить</Button></Alert> : null}
        {selectedItem && !activeItem && !detailError ? <InlineLoader label="Загрузка обращения" /> : null}
        <Tabs className="quality-tabs" value={tab} onChange={setTab} keepMounted={false} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>


          <Tabs.Panel className="quality-tab-panel" value="check" keepMounted pt={focusedReview ? 0 : 'xs'} style={{ flex: 1, minHeight: 0 }}>
            <Stack gap="sm" style={{ height: '100%', minHeight: 0 }}>
              {!activeSession ? <Card className="quality-start-card" withBorder radius="sm" p="sm">
                <Group className="quality-start-form" align="end" gap="sm" wrap="wrap" mt={0}>
                  <Select
                    label="Кого проверяем"
                    data={targetOptions}
                    value={targetValue}
                    onChange={(v) => {
                      const next = v || 'ai:'
                      setTargetValue(next)
                      if (next.startsWith('anonymous:')) setEvaluationMode('manual')
                    }}
                    searchable
                    style={{ minWidth: 220, flex: '1 1 220px' }}
                  />
                  {anonymousModeSelected ? (
                    <MultiSelect
                      label="Роли в выборке"
                      description="Имена авторов будут скрыты до завершения проверки"
                      data={QUALITY_ANONYMOUS_ROLE_OPTIONS}
                      value={anonymousRoleFilters}
                      onChange={setAnonymousRoleFilters}
                      searchable
                      clearable
                      style={{ minWidth: 260, flex: '1 1 260px' }}
                    />
                  ) : null}
                  <Checkbox
                    label="Проверка АИ"
                    description={
                      anonymousModeSelected
                        ? 'Обезличенный режим проверяется вручную'
                        : aiSettings?.enabled
                        ? targetValue.startsWith('ai:')
                          ? 'Оценка Мозгобота отдельным промптом и критериями'
                          : 'Оценка оператора через AI по критериям операторов'
                        : 'Сначала включите AI в настройках АРМ'
                    }
                    checked={evaluationMode === 'ai'}
                    disabled={activeSession || anonymousModeSelected || !aiSettings?.enabled}
                    onChange={(event) => setEvaluationMode(event.currentTarget.checked ? 'ai' : 'manual')}
                    mt={4}
                    style={{ minWidth: 180, flex: '1 1 180px' }}
                  />
                  <MultiSelect
                    label="Проекты"
                    data={projectOptions}
                    value={projectIds}
                    onChange={setProjectIds}
                    placeholder="Все проекты"
                    searchable
                    clearable
                    style={{ minWidth: 280, flex: '2 1 280px' }}
                  />
                  {evaluationMode === 'ai' && projectIds.length ? (
                    <Stack gap={4} style={{ flex: '1 1 100%', minWidth: 280 }}>
                      <Text size="xs" fw={600}>Особые условия по проектам</Text>
                      <Text size="xs" c="dimmed">Необязательно. Эти инструкции добавятся только к обращениям соответствующего проекта и сохранятся в отчете.</Text>
                      {projectIds.map((projectId) => {
                        const project = projects.find((item) => item.id === projectId)
                        return (
                          <Textarea
                            key={projectId}
                            label={project?.name || projectId}
                            placeholder="Например: для этого проекта отдельно проверяйте соблюдение правил тарифа..."
                            minRows={2}
                            autosize
                            value={aiProjectInstructions[projectId] || ''}
                            onChange={({ currentTarget: { value } }) => setAiProjectInstructions((current) => ({ ...current, [projectId]: value }))}
                          />
                        )
                      })}
                    </Stack>
                  ) : null}
                  <Select
                    label="Период обращений"
                    data={[
                      { value: 'today', label: 'Сегодня' },
                      { value: 'week', label: 'Последние 7 дней' },
                      { value: 'month', label: 'Последние 30 дней' },
                      { value: 'previous_month', label: 'Прошлый календарный месяц' },
                      { value: 'custom', label: 'Свой период' },
                    ]}
                    value={period}
                    onChange={(value) => setPeriod((value as typeof period) || 'week')}
                    style={{ minWidth: 220, flex: '1 1 220px' }}
                  />
                  {period === 'custom' ? (
                    <Box className="quality-custom-period">
                      <Text size="xs" fw={600}>Свой период обращений</Text>
                      <Text size="xs" c="dimmed">В выборку попадут закрытые обращения, созданные в выбранном диапазоне по московскому времени.</Text>
                      <Group className="quality-custom-period-fields" gap="sm" wrap="wrap" align="end" mt={6}>
                        <TextInput label="С даты" placeholder="ДД.ММ.ГГГГ" type="date" value={periodStartDate} onChange={(event) => setPeriodStartDate(event.currentTarget.value)} />
                        <TextInput label="По дату включительно" placeholder="ДД.ММ.ГГГГ" type="date" value={periodEndDate} onChange={(event) => setPeriodEndDate(event.currentTarget.value)} />
                      </Group>
                    </Box>
                  ) : null}
                  <Select
                    label="Способ выборки"
                    data={[
                      { value: 'percent', label: 'Процент обращений' },
                      { value: 'count', label: 'Количество обращений' },
                    ]}
                    value={sampleMode}
                    onChange={(value) => setSampleMode(value === 'count' ? 'count' : 'percent')}
                    style={{ minWidth: 190, flex: '1 1 190px' }}
                  />
                  {sampleMode === 'percent' ? (
                    <NumberInput
                      label="Процент выборки"
                      min={1}
                      max={100}
                      value={samplePercent}
                      onChange={(v) => setSamplePercent(Number(v) || 1)}
                      w={140}
                    />
                  ) : (
                    <NumberInput
                      label="Количество обращений"
                      min={1}
                      max={10000}
                      value={sampleCount}
                      onChange={(v) => setSampleCount(Number(v) || 1)}
                      w={170}
                    />
                  )}
                  {evaluationMode === 'ai' ? (
                    <NumberInput
                      label="Размер батча"
                      description="Сколько обращений обрабатывается между сохранениями прогресса"
                      min={1}
                      max={20}
                      value={aiBatchSize}
                      onChange={(v) => setAiBatchSize(Number(v) || 1)}
                      disabled={activeSession}
                      w={190}
                    />
                  ) : null}
                  {!activeSession ? (
                    <Button leftSection={<IconPlayerPlay size={16} />} loading={busy} onClick={() => void startSession()}>
                      Запустить
                    </Button>
                  ) : (
                    <Badge size="lg" color={session?.evaluation_mode === 'ai' ? 'violet' : 'blue'} variant="light">
                      {session?.evaluation_mode === 'ai' ? 'AI-проверка в работе' : 'Проверка в работе'}
                    </Badge>
                  )}
                </Group>
              </Card> : null}
              {session ? (
                <>
                <Group className="quality-mobile-toolbar" gap="xs" wrap="nowrap">{sectionMenu}<SegmentedControl className="quality-mobile-panes" fullWidth size="xs" value={mobilePane} onChange={setMobilePane} aria-label="Рабочая область проверки" data={[
                  { value: 'list', label: `Обращения · ${items.length}` },
                  { value: 'conversation', label: 'Переписка' },
                  { value: 'review', label: 'Оценки' },
                ]} /></Group>
                <Box className="quality-check-layout" data-mobile-pane={mobilePane}>
                  <Box className="quality-check-column quality-check-column--list">
                  <Card className="quality-check-card" withBorder radius="sm" p="sm" style={{ minHeight: 0 }}>
                    <Stack gap="sm" h="100%">
                      <Group gap="xs" wrap="nowrap">
                        {sectionMenu}
                        <Text size="sm" fw={700}>Обращения · {doneCount}/{items.length}</Text>
                      </Group>
                      <Box className="quality-session-list-head">
                        <Text fw={700}>{session.target_label}</Text>
                        <Text size="xs" c="dimmed">{session.project_label || 'Все проекты'}</Text>
                        {session.target_type === 'anonymous' ? (
                          <Badge size="xs" color="orange" variant="light">
                            Обезличенно · {qualityAnonymousRolesLabel(session.anonymous_role_filters)}
                          </Badge>
                        ) : null}
                        <Text size="xs" c="dimmed" mt={3}>
                          {session.period_label || 'Период не указан'} · найдено: {session.total_candidates ?? 0} · в выборке: {session.sample_size}
                        </Text>
                      </Box>
                      <Progress value={session.evaluation_mode === 'ai' && session.ai_total ? Math.round(((session.ai_completed || 0) + (session.ai_failed || 0)) / session.ai_total * 100) : progress} />
                      {session.evaluation_mode === 'ai' && session.ai_error ? (
                        <Alert color="orange" title="Проблема AI-очереди">
                          {session.ai_error}
                        </Alert>
                      ) : null}
                      {session.evaluation_mode === 'ai' ? (
                        <Text size="xs" c="dimmed">
                          AI-статус: {session.ai_status || '—'} · готово {session.ai_completed || 0}/
                          {session.ai_total || session.sample_size} · ошибок: {session.ai_failed || 0}
                          {(session.ai_status === 'queued' || session.ai_status === 'running') &&
                          !(session.ai_completed || 0) &&
                          !(session.ai_failed || 0)
                            ? ' · ждём воркер очереди ai'
                            : ''}
                        </Text>
                      ) : null}
                      <Group className="quality-session-stats" gap={6}>
                        <Badge variant="light">Всего: {items.length}</Badge>
                        <Badge color="green" variant="light">Оценено: {reviewedCount}</Badge>
                        <Badge color="gray" variant="light">Пропущено: {skippedCount}</Badge>
                        {session.evaluation_mode === 'ai' ? <Badge color="red" variant="light">Ошибки: {session.ai_failed || 0}</Badge> : null}
                      </Group>
                      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto">
                        <Stack gap={4}>
                          {items.map((item, index) => (
                            <Button
                              key={item.id}
                              className="quality-item-button"
                              variant={index === activeIndex ? 'light' : 'subtle'}
                              color={item.ai_status === 'failed' ? 'red' : item.status === 'reviewed' ? 'green' : item.status === 'skipped' ? 'gray' : 'blue'}
                              justify="space-between"
                              size="compact-sm"
                              onClick={() => { setActiveIndex(index); setMobilePane('conversation') }}
                              rightSection={item.weighted_score != null ? `${item.weighted_score}%` : undefined}
                            >
                              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, width: '100%' }}>
                                <Text size="sm" fw={600} truncate style={{ flex: 1, minWidth: 0 }}>
                                  #{item.ticket?.ticket_number || item.position} · {item.ticket?.subject || 'Без темы'}
                                </Text>
                                <Text className="quality-item-date" size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                                  {item.ticket?.created_at ? formatDateTimeDot(item.ticket.created_at) : '—'}
                                </Text>
                              </Group>
                            </Button>
                          ))}
                        </Stack>
                      </ScrollArea>
                      {activeSession ? (
                        <Button variant="light" color="orange" disabled={!items.length} loading={busy} onClick={() => void cancelSession()}>
                          Остановить проверку
                        </Button>
                      ) : null}
                      {!activeSession && session.status === 'canceled' && (
                        session.evaluation_mode === 'ai'
                          ? items.some((item) => item.ai_status !== 'completed' && item.ai_status !== 'failed')
                          : items.some((item) => item.status !== 'reviewed' && item.status !== 'skipped')
                      ) ? (
                        <Button variant="light" color="blue" loading={busy} onClick={() => void resumeSession()}>
                          Возобновить проверку
                        </Button>
                      ) : null}
                      {session.evaluation_mode !== 'ai' ? (
                        <Button variant="light" color="green" disabled={!items.length} loading={busy} onClick={() => void completeSession()}>
                          Завершить проверку
                        </Button>
                      ) : null}
                    </Stack>
                  </Card>
                  </Box>
                  {session.evaluation_mode === 'ai' ? (
                    <>
                      <Box className="quality-check-column quality-check-column--conversation">
                        <Card className="quality-check-card" withBorder radius="sm" p="sm" style={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
                          <QualityConversation
                            item={activeItem}
                            onIncorrectResponseCreated={onIncorrectResponseCreated}
                          />
                        </Card>
                      </Box>
                      <Box className="quality-check-column quality-check-column--review">
                        <Card className="quality-check-card quality-ai-review-card" withBorder radius="sm" p="sm" style={{ minWidth: 0, minHeight: 0, overflowY: 'auto' }}>
                          <Stack gap="sm">
                            <Group justify="space-between" gap="xs" wrap="nowrap">
                              <Box style={{ minWidth: 0 }}>
                                <Text fw={700}>Результат AI-проверки</Text>
                                <Text size="xs" c="dimmed">Оценка и комментарии модели</Text>
                              </Box>
                              <Badge color={activeItem?.ai_status === 'failed' ? 'red' : activeItem?.ai_status === 'completed' ? 'green' : 'blue'}>
                                {activeItem?.ai_status || 'pending'}
                              </Badge>
                            </Group>
                            <Progress value={session.ai_total ? Math.round(((session.ai_completed || 0) + (session.ai_failed || 0)) / session.ai_total * 100) : 0} />
                            {activeItem?.ai_error ? <Alert color="red" title="Ошибка обработки">{activeItem.ai_error}</Alert> : null}
                            <AiResponseView item={activeItem} criteria={criteria} />
                            <Divider />
                            <Accordion variant="contained" radius="sm">
                              <Accordion.Item value="ai-request">
                                <Accordion.Control>
                                  <Box>
                                    <Text fw={700}>Что отправлено модели</Text>
                                    <Text size="xs" c="dimmed">Раскрыть полный запрос и части диалога</Text>
                                  </Box>
                                </Accordion.Control>
                                <Accordion.Panel>
                                  <Text size="xs" c="dimmed" mb="sm">Полный запрос разбит на части только для обработки. Нажмите на часть, чтобы раскрыть содержимое.</Text>
                                  <AiRequestView request={activeItem?.ai_request} />
                                </Accordion.Panel>
                              </Accordion.Item>
                            </Accordion>
                          </Stack>
                        </Card>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box className="quality-check-column quality-check-column--conversation">
                        <Card className="quality-check-card" withBorder radius="sm" p="sm" style={{ minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
                          <QualityConversation
                            item={activeItem}
                            onIncorrectResponseCreated={onIncorrectResponseCreated}
                          />
                        </Card>
                      </Box>
                      <Box className="quality-check-column quality-check-column--review">
                        <Card className="quality-check-card" withBorder radius="sm" p="sm" style={{ minWidth: 0, minHeight: 0, overflowY: 'auto' }}>
                          {detailError ? null : !activeItem ? <Text>Загрузка обращения…</Text> : <QualityReviewForm
                            criteria={criteria}
                            item={activeItem}
                            saving={busy}
                            onSubmit={(payload) => void submitScore(payload)}
                          />}
                        </Card>
                      </Box>
                    </>
                  )}
                </Box>
                </>
              ) : (
                <EmptyState title="Проверка не запущена" description="Выберите оператора или Мозгобота, проекты и размер выборки." />
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel className="quality-tab-panel quality-section--reports" value="reports" pt="sm">
            <Stack gap="sm">
              <Card withBorder radius="sm" p="sm">
                <Group align="end" gap="sm" wrap="wrap">
                  <NativeSelect
                    label="Период"
                    value={String(reportDays)}
                    onChange={(event) => setReportDays(Number(event.currentTarget.value))}
                    data={[
                      { value: '7', label: '7 дней' },
                      { value: '30', label: '30 дней' },
                      { value: '90', label: '90 дней' },
                    ]}
                  />
                  <Button variant="light" loading={busy} onClick={() => void loadReports()}>Показать</Button>
                  <Badge size="lg" color={scoreColor(reports?.average_score)} variant="light">
                    Средний балл: {reports?.average_score ?? '—'}%
                  </Badge>
                  <Badge size="lg" variant="light">Проверок: {reports?.sessions_count ?? 0}</Badge>
                </Group>
              </Card>
              <Table.ScrollContainer minWidth={0}>
                <Table className="quality-results-table" striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Дата</Table.Th>
                      <Table.Th>Проверяющий</Table.Th>
                      <Table.Th>Кого проверяли</Table.Th>
                      <Table.Th>Проекты</Table.Th>
                      <Table.Th>Оценено</Table.Th>
                      <Table.Th>Средний балл</Table.Th>
                      <Table.Th>Рекомендация</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(reports?.items ?? []).map((row) => (
                      <Table.Tr
                        key={row.session_id}
                        onClick={() => void openReportDetail(row.session_id)}
                        style={{ cursor: 'pointer' }}
                        tabIndex={0}
                        onKeyDown={(event) => { if (event.key === 'Enter') void openReportDetail(row.session_id) }}
                        title="Открыть детальный отчет"
                      >
                        <Table.Td data-label="Дата">{row.created_at ? formatDateTimeDot(row.created_at) : '—'}</Table.Td>
                        <Table.Td data-label="Проверяющий">{row.reviewer_name || '—'}</Table.Td>
                        <Table.Td data-label="Кого проверяли">
                        <Text>{row.target_label}</Text>
                          {row.target_type === 'anonymous' ? <Badge color="orange" variant="light">Обезличенно</Badge> : null}
                          {row.evaluation_mode === 'ai' ? <Badge color="violet" variant="light">AI · {row.ai_completed || 0}/{row.ai_total || row.items_total}</Badge> : null}
                        </Table.Td>
                        <Table.Td data-label="Проекты">{row.project_label || 'Все проекты'}</Table.Td>
                        <Table.Td data-label="Оценено">{row.reviewed} / {row.items_total}</Table.Td>
                        <Table.Td data-label="Средний балл">
                          <Badge color={scoreColor(row.average_score)} variant="light">{row.average_score ?? '—'}%</Badge>
                        </Table.Td>
                        <Table.Td data-label="Рекомендация" maw={360}>
                          <Text size="sm" lineClamp={2}>{row.recommendation || '—'}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel className="quality-tab-panel quality-section--incorrect" value="incorrect" pt="sm">
            <Stack gap="sm">
              <Card withBorder radius="sm" p="sm">
                <Group align="end" gap="sm" wrap="wrap">
                  <NativeSelect
                    label="Период"
                    value={String(reportDays)}
                    onChange={(event) => setReportDays(Number(event.currentTarget.value))}
                    data={[
                      { value: '7', label: '7 дней' },
                      { value: '30', label: '30 дней' },
                      { value: '90', label: '90 дней' },
                    ]}
                  />
                  <Button variant="light" loading={incorrectResponsesLoading} onClick={() => void loadIncorrectResponses()}>
                    Показать
                  </Button>
                  <Button
                    variant="light"
                    leftSection={<IconDownload size={16} />}
                    loading={incorrectResponsesExporting}
                    onClick={() => void downloadIncorrectResponses()}
                  >
                    HTML
                  </Button>
                  <Badge size="lg" color="orange" variant="light">
                    Отметок: {incorrectResponses?.total ?? 0}
                  </Badge>
                </Group>
              </Card>
              {incorrectResponsesLoading ? (
                <InlineLoader label="Загрузка некорректных ответов" />
              ) : (incorrectResponses?.items?.length ? (
                <Table.ScrollContainer minWidth={0}>
                  <Table className="quality-results-table" striped highlightOnHover withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Тикет</Table.Th>
                        <Table.Th>Ответ оператора</Table.Th>
                        <Table.Th>Оператор ответа</Table.Th>
                        <Table.Th>Кого проверяли</Table.Th>
                        <Table.Th>Кто проверял</Table.Th>
                        <Table.Th>Комментарий</Table.Th>
                        <Table.Th>Дата отметки</Table.Th>
                        <Table.Th>Переход</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {incorrectResponses.items.map((row) => (
                        <Table.Tr key={row.id}>
                          <Table.Td data-label="Тикет">
                            <Text size="sm" fw={700}>
                              {row.ticket_number != null ? `#${row.ticket_number}` : row.ticket_id.slice(0, 8)}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={2}>{row.ticket_subject || 'Без темы'}</Text>
                          </Table.Td>
                          <Table.Td data-label="Ответ оператора" maw={320}>
                            <Text size="sm" lineClamp={4} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                              {row.message_content || '—'}
                            </Text>
                          </Table.Td>
                          <Table.Td data-label="Оператор ответа">{row.operator_name || '—'}</Table.Td>
                          <Table.Td data-label="Кого проверяли">{row.target_operator_name || '—'}</Table.Td>
                          <Table.Td data-label="Кто проверял">{row.reviewer_name || '—'}</Table.Td>
                          <Table.Td data-label="Комментарий" maw={300}>
                            <Text size="sm" lineClamp={4} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                              {row.comment || '—'}
                            </Text>
                          </Table.Td>
                          <Table.Td data-label="Дата отметки">{row.created_at ? formatDateTimeDot(row.created_at) : '—'}</Table.Td>
                          <Table.Td data-label="Переход">
                            <Button
                              size="compact-xs"
                              variant="light"
                              leftSection={<IconExternalLink size={14} />}
                              onClick={() => openTicketFromIncorrectResponse(row)}
                            >
                              В тикет
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              ) : (
                <EmptyState title="Некорректных ответов нет" description="Отметки появятся после сохранения комментариев у ответов операторов." />
              ))}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel className="quality-tab-panel quality-section--history" value="history" pt="sm">
            <Stack gap="sm">
              {history.length ? history.map((row) => (
                <Card key={row.id} withBorder radius="sm" p="sm">
                  <Group justify="space-between" gap="xs" wrap="wrap">
                    <Box style={{ minWidth: 0 }}>
                      <Group gap={6}>
                        <Text fw={700}>{row.target_label}</Text>
                        <Badge color={row.status === 'completed' ? 'green' : 'blue'} variant="light">
                          {row.status === 'completed' ? 'завершена' : row.status === 'canceled' ? 'остановлена' : 'в работе'}
                        </Badge>
                        {row.target_type === 'anonymous' ? <Badge color="orange" variant="light">Обезличенно</Badge> : null}
                        {row.evaluation_mode === 'ai' ? <Badge color="violet" variant="light">AI</Badge> : null}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {row.created_at ? formatDateTimeDot(row.created_at) : '—'} · {row.reviewer_name || '—'} · {row.project_label || 'Все проекты'} · {row.period_label || 'Период не указан'}
                      </Text>
                    </Box>
                    <Group gap={6} wrap="wrap">
                      <Badge variant="light">За период: {row.total_candidates ?? 0}</Badge>
                      <Badge variant="light">Обращений: {row.sample_size}</Badge>
                      <Badge color={scoreColor(row.summary?.average_score)} variant="light">
                        {row.summary?.average_score ?? '—'}%
                      </Badge>
                      {isResumable(row) ? (
                        <Button
                          size="compact-xs"
                          variant="light"
                          color="blue"
                          loading={busy}
                          onClick={() => void resumeSession(row.id)}
                        >
                          Возобновить
                        </Button>
                      ) : null}
                    </Group>
                  </Group>
                  {row.recommendation ? <Text size="sm" mt="xs">{row.recommendation}</Text> : null}
                </Card>
              )) : <EmptyState title="История пуста" description="Пока нет созданных проверок качества." />}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel className="quality-tab-panel quality-section--settings" value="settings" pt="sm">
            {settings ? (
              <Stack gap="sm">
                <Tabs
                  value={settingsTab}
                  onChange={setSettingsTab}
                  keepMounted={false}
                  className="quality-settings-tabs"
                >


                  <Tabs.Panel value="general" pt="sm">
                    <Card withBorder radius="sm" p="md">
                      <Stack gap="md">
                        <Box>
                          <Text fw={700}>Общие параметры выборки</Text>
                          <Text size="xs" c="dimmed">Базовые значения для новых проверок качества.</Text>
                        </Box>
                        <Group gap="sm" align="end" wrap="wrap">
                          <NumberInput
                            label="Выборка по умолчанию, %"
                            min={1}
                            max={100}
                            value={settings.default_sample_percent}
                            onChange={(v) => setSettings((s) => s ? { ...s, default_sample_percent: Number(v) || 1 } : s)}
                            style={{ flex: '1 1 220px' }}
                          />
                          <NumberInput
                            label="Минимум закрытых"
                            min={1}
                            max={1000}
                            value={settings.min_closed_tickets}
                            onChange={(v) => setSettings((s) => s ? { ...s, min_closed_tickets: Number(v) || 1 } : s)}
                            style={{ flex: '1 1 220px' }}
                          />
                          <Button loading={busy} onClick={() => void saveSettings()}>Сохранить</Button>
                        </Group>
                      </Stack>
                    </Card>
                  </Tabs.Panel>

                  <Tabs.Panel value="operators" pt="sm">
                    <Stack gap="sm">
                      <Group justify="space-between" align="center">
                        <Box>
                          <Text fw={700}>Критерии оценки операторов</Text>
                          <Text size="xs" c="dimmed">Используются при проверке живого оператора вручную или через AI.</Text>
                        </Box>
                        <Group gap="xs">
                          {settings.can_manage_default_criteria ? (
                            <Button
                              leftSection={<IconDeviceFloppy size={16} />}
                              variant="light"
                              color="blue"
                              loading={busy}
                              onClick={() => void saveCriteriaAsDefaults('operators')}
                            >
                              Сделать стандартом
                            </Button>
                          ) : null}
                          <Button
                            leftSection={<IconEye size={16} />}
                            variant="default"
                            onClick={() => setStandardPreview('operators')}
                          >
                            Посмотреть стандарт
                          </Button>
                          <Button
                            leftSection={<IconRotateClockwise size={16} />}
                            variant="light"
                            color="orange"
                            loading={busy}
                            onClick={() => resetCriteriaToDefaults('operators')}
                          >
                            Вернуть к стандартным
                          </Button>
                          <Button variant="default" loading={busy} onClick={() => void saveSettings()}>
                            Сохранить критерии
                          </Button>
                          <Button
                            leftSection={<IconPlus size={16} />}
                            variant="light"
                            onClick={() => setSettings((s) => s ? {
                              ...s,
                              criteria: [
                                ...s.criteria,
                                {
                                  key: `criterion_${Date.now()}`,
                                  label: 'Новый критерий',
                                  weight: 1,
                                  enabled: true,
                                  include_in_total: true,
                                  allow_skip: true,
                                  type: 'scale',
                                  scope: 'operator',
                                  option_weights: { yes: 1, no: 0, na: 0 },
                                },
                              ],
                            } : s)}
                          >
                            Добавить критерий
                          </Button>
                        </Group>
                      </Group>
                      <Accordion multiple variant="separated" className="quality-criteria-editor">
                  {(settings.criteria ?? []).map((criterion, index) => (
                    <Accordion.Item key={`op-${index}`} value={`op-${index}`}>
                      <Accordion.Control>
                        <Group gap="xs" wrap="wrap">
                          <Text size="sm" fw={600}>{criterion.label || 'Новый критерий'}</Text>
                          <Badge size="xs" color={criterion.enabled ? 'blue' : 'gray'} variant="light">{criterion.enabled ? 'Включен' : 'Выключен'}</Badge>
                          <Text size="xs" c="dimmed">{criterion.type === 'scale' ? 'Шкала 1–5' : criterion.type === 'boolean' ? 'Да / Нет' : 'Да / Нет / н/д'} · вес {criterion.weight}</Text>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel><Box className="quality-criterion-editor">
                      <Group gap="sm" align="end" wrap="wrap">
                        <Checkbox
                          label="Вкл"
                          checked={criterion.enabled}
                          onChange={({ currentTarget: { checked } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria = [...s.criteria]
                            criteria[index] = { ...criteria[index], enabled: checked }
                            return { ...s, criteria }
                          })}
                        />
                        <Checkbox
                          label="В итог"
                          checked={criterion.include_in_total !== false}
                          onChange={({ currentTarget: { checked } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria = [...s.criteria]
                            criteria[index] = { ...criteria[index], include_in_total: checked }
                            return { ...s, criteria }
                          })}
                        />
                        <Checkbox
                          label="Можно пропустить"
                          checked={criterion.allow_skip !== false}
                          onChange={({ currentTarget: { checked } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria = [...s.criteria]
                            criteria[index] = { ...criteria[index], allow_skip: checked }
                            return { ...s, criteria }
                          })}
                        />
                        <TextInput
                          label="Ключ"
                          value={criterion.key}
                          onChange={({ currentTarget: { value } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria = [...s.criteria]
                            criteria[index] = { ...criteria[index], key: value }
                            return { ...s, criteria }
                          })}
                          style={{ width: 190, flex: '1 1 190px' }}
                        />
                        <TextInput
                          label="Название"
                          value={criterion.label}
                          onChange={({ currentTarget: { value } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria = [...s.criteria]
                            criteria[index] = { ...criteria[index], label: value }
                            return { ...s, criteria }
                          })}
                          style={{ flex: '2 1 240px' }}
                        />
                        <Select
                          label="Тип оценки"
                          value={criterion.type}
                          data={[
                            { value: 'scale', label: 'Шкала 1–5' },
                            { value: 'boolean', label: 'Да / Нет' },
                            { value: 'weighted_choice', label: 'Да / Нет / н/д' },
                          ]}
                          onChange={(value) => setSettings((s) => {
                            if (!s) return s
                            const criteria = [...s.criteria]
                            criteria[index] = {
                              ...criteria[index],
                              type: value === 'boolean' || value === 'weighted_choice' ? value : 'scale',
                              option_weights: criteria[index].option_weights || { yes: criteria[index].weight || 1, no: 0, na: 0 },
                            }
                            return { ...s, criteria }
                          })}
                          style={{ width: 160, flex: '1 1 160px' }}
                        />
                        <Select
                          label="Область оценки"
                          description="Кого оцениваем"
                          value={criterion.scope || 'operator'}
                          data={[
                            { value: 'operator', label: 'Выбранный оператор' },
                            { value: 'ticket', label: 'Весь тикет' },
                          ]}
                          onChange={(value) => setSettings((s) => {
                            if (!s) return s
                            const criteria = [...s.criteria]
                            criteria[index] = { ...criteria[index], scope: value === 'ticket' ? 'ticket' : 'operator' }
                            return { ...s, criteria }
                          })}
                          style={{ width: 190, flex: '1 1 190px' }}
                        />
                        <NumberInput
                          label="Вес"
                          min={0}
                          max={10}
                          value={criterion.weight}
                          onChange={(v) => setSettings((s) => {
                            if (!s) return s
                            const criteria = [...s.criteria]
                            criteria[index] = { ...criteria[index], weight: Number(v) || 0 }
                            return { ...s, criteria }
                          })}
                          w={100}
                        />
                        {criterion.type === 'weighted_choice' ? (
                          <>
                            {(['yes', 'no', 'na'] as const).map((option) => (
                              <NumberInput
                                key={option}
                                label={option === 'yes' ? 'Вес Да' : option === 'no' ? 'Вес Нет' : 'Вес н/д'}
                                min={0}
                                max={10}
                                step={1}
                                value={optionWeight(criterion, option)}
                                onChange={(v) => setSettings((s) => {
                                  if (!s) return s
                                  const criteria = [...s.criteria]
                                  const current = criteria[index]
                                  criteria[index] = {
                                    ...current,
                                    option_weights: {
                                      yes: optionWeight(current, 'yes'),
                                      no: optionWeight(current, 'no'),
                                      na: optionWeight(current, 'na'),
                                      [option]: Number(v) || 0,
                                    },
                                  }
                                  return { ...s, criteria }
                                })}
                                w={105}
                              />
                            ))}
                          </>
                        ) : null}
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label="Удалить критерий"
                          title="Удалить критерий"
                          disabled={settings.criteria.length <= 1}
                          onClick={() => setSettings((s) => s ? {
                            ...s,
                            criteria: s.criteria.filter((_, itemIndex) => itemIndex !== index),
                          } : s)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Box></Accordion.Panel>
                    </Accordion.Item>
                  ))}
                      </Accordion>
                    </Stack>
                  </Tabs.Panel>

                  <Tabs.Panel value="bot" pt="sm">
                    <Stack gap="sm">
                <Group justify="space-between" align="center">
                  <Box>
                    <Text fw={700}>Критерии оценки Мозгобота</Text>
                    <Text size="xs" c="dimmed">Отдельный профиль: следование инструкциям, сбор данных, передача оператору. Подходит и для тикетов, где бот участвовал до оператора.</Text>
                  </Box>
                  <Group gap="xs">
                    {settings.can_manage_default_criteria ? (
                      <Button
                        leftSection={<IconDeviceFloppy size={16} />}
                        variant="light"
                        color="blue"
                        loading={busy}
                        onClick={() => void saveCriteriaAsDefaults('bot')}
                      >
                        Сделать стандартом
                      </Button>
                    ) : null}
                    <Button
                      leftSection={<IconEye size={16} />}
                      variant="default"
                      onClick={() => setStandardPreview('bot')}
                    >
                      Посмотреть стандарт
                    </Button>
                    <Button
                      leftSection={<IconRotateClockwise size={16} />}
                      variant="light"
                      color="orange"
                      loading={busy}
                      onClick={() => resetCriteriaToDefaults('bot')}
                    >
                      Вернуть к стандартным
                    </Button>
                    <Button variant="default" loading={busy} onClick={() => void saveSettings()}>
                      Сохранить критерии
                    </Button>
                    <Button
                      leftSection={<IconPlus size={16} />}
                      variant="light"
                      onClick={() => setSettings((s) => s ? {
                        ...s,
                        criteria_ai: [
                          ...(s.criteria_ai || []),
                          {
                            key: `ai_criterion_${Date.now()}`,
                            label: 'Новый критерий Мозгобота',
                            weight: 2,
                            enabled: true,
                            include_in_total: true,
                            allow_skip: true,
                            type: 'scale',
                            scope: 'ticket',
                            option_weights: { yes: 2, no: 0, na: 0 },
                          },
                        ],
                      } : s)}
                    >
                      Добавить критерий
                    </Button>
                  </Group>
                </Group>
                <Accordion multiple variant="separated" className="quality-criteria-editor">
                  {(settings.criteria_ai ?? []).map((criterion, index) => (
                    <Accordion.Item key={`ai-${index}`} value={`ai-${index}`}>
                      <Accordion.Control><Group gap="xs" wrap="wrap">
                        <Text size="sm" fw={600}>{criterion.label || 'Новый критерий'}</Text>
                        <Badge size="xs" color={criterion.enabled ? 'blue' : 'gray'} variant="light">{criterion.enabled ? 'Включен' : 'Выключен'}</Badge>
                        <Text size="xs" c="dimmed">{criterion.type === 'scale' ? 'Шкала 1–5' : criterion.type === 'boolean' ? 'Да / Нет' : 'Да / Нет / н/д'} · вес {criterion.weight}</Text>
                      </Group></Accordion.Control>
                      <Accordion.Panel><Box className="quality-criterion-editor">
                      <Group gap="sm" align="end" wrap="wrap">
                        <Checkbox
                          label="Вкл"
                          checked={criterion.enabled}
                          onChange={({ currentTarget: { checked } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria_ai = [...(s.criteria_ai || [])]
                            criteria_ai[index] = { ...criteria_ai[index], enabled: checked }
                            return { ...s, criteria_ai }
                          })}
                        />
                        <Checkbox
                          label="В итог"
                          checked={criterion.include_in_total !== false}
                          onChange={({ currentTarget: { checked } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria_ai = [...(s.criteria_ai || [])]
                            criteria_ai[index] = { ...criteria_ai[index], include_in_total: checked }
                            return { ...s, criteria_ai }
                          })}
                        />
                        <Checkbox
                          label="Можно пропустить"
                          checked={criterion.allow_skip !== false}
                          onChange={({ currentTarget: { checked } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria_ai = [...(s.criteria_ai || [])]
                            criteria_ai[index] = { ...criteria_ai[index], allow_skip: checked }
                            return { ...s, criteria_ai }
                          })}
                        />
                        <TextInput
                          label="Ключ"
                          value={criterion.key}
                          onChange={({ currentTarget: { value } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria_ai = [...(s.criteria_ai || [])]
                            criteria_ai[index] = { ...criteria_ai[index], key: value }
                            return { ...s, criteria_ai }
                          })}
                          style={{ width: 190, flex: '1 1 190px' }}
                        />
                        <TextInput
                          label="Название"
                          value={criterion.label}
                          onChange={({ currentTarget: { value } }) => setSettings((s) => {
                            if (!s) return s
                            const criteria_ai = [...(s.criteria_ai || [])]
                            criteria_ai[index] = { ...criteria_ai[index], label: value }
                            return { ...s, criteria_ai }
                          })}
                          style={{ flex: '2 1 240px' }}
                        />
                        <Select
                          label="Тип оценки"
                          value={criterion.type}
                          data={[
                            { value: 'scale', label: 'Шкала 1–5' },
                            { value: 'boolean', label: 'Да / Нет' },
                            { value: 'weighted_choice', label: 'Да / Нет / н/д' },
                          ]}
                          onChange={(value) => setSettings((s) => {
                            if (!s) return s
                            const criteria_ai = [...(s.criteria_ai || [])]
                            criteria_ai[index] = {
                              ...criteria_ai[index],
                              type: value === 'boolean' || value === 'weighted_choice' ? value : 'scale',
                              option_weights: criteria_ai[index].option_weights || { yes: criteria_ai[index].weight || 1, no: 0, na: 0 },
                            }
                            return { ...s, criteria_ai }
                          })}
                          style={{ width: 160, flex: '1 1 160px' }}
                        />
                        <NumberInput
                          label="Вес"
                          min={0}
                          max={10}
                          value={criterion.weight}
                          onChange={(v) => setSettings((s) => {
                            if (!s) return s
                            const criteria_ai = [...(s.criteria_ai || [])]
                            criteria_ai[index] = { ...criteria_ai[index], weight: Number(v) || 0 }
                            return { ...s, criteria_ai }
                          })}
                          w={100}
                        />
                        {criterion.type === 'weighted_choice' ? (
                          <>
                            {(['yes', 'no', 'na'] as const).map((option) => (
                              <NumberInput
                                key={option}
                                label={option === 'yes' ? 'Вес Да' : option === 'no' ? 'Вес Нет' : 'Вес н/д'}
                                min={0}
                                max={10}
                                step={1}
                                value={optionWeight(criterion, option)}
                                onChange={(v) => setSettings((s) => {
                                  if (!s) return s
                                  const criteria_ai = [...(s.criteria_ai || [])]
                                  const current = criteria_ai[index]
                                  criteria_ai[index] = {
                                    ...current,
                                    option_weights: {
                                      yes: optionWeight(current, 'yes'),
                                      no: optionWeight(current, 'no'),
                                      na: optionWeight(current, 'na'),
                                      [option]: Number(v) || 0,
                                    },
                                  }
                                  return { ...s, criteria_ai }
                                })}
                                w={105}
                              />
                            ))}
                          </>
                        ) : null}
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label="Удалить критерий Мозгобота"
                          title="Удалить критерий Мозгобота"
                          disabled={(settings.criteria_ai || []).length <= 1}
                          onClick={() => setSettings((s) => s ? {
                            ...s,
                            criteria_ai: (s.criteria_ai || []).filter((_, itemIndex) => itemIndex !== index),
                          } : s)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Box></Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
                    </Stack>
                  </Tabs.Panel>

                  <Tabs.Panel value="ai" pt="sm">
                <Card withBorder radius="sm" p="sm">
                  {aiSettings ? (
                    <Stack gap="sm">
                      <Box>
                        <Text fw={700}>AI-проверка</Text>
                        <Text size="xs" c="dimmed">Для операторов и Мозгобота — разные промпты и критерии. Endpoint vLLM общий.</Text>
                      </Box>
                      <Group justify="space-between" align="center">
                        <Badge color={aiSettings.enabled ? 'green' : 'gray'} variant="light">{aiSettings.enabled ? 'Включено' : 'Выключено'}</Badge>
                        <Checkbox label="Разрешить AI-проверку" checked={aiSettings.enabled} onChange={(event) => setAiSettings({ ...aiSettings, enabled: event.currentTarget.checked })} />
                      </Group>
                      <Group className="quality-ai-settings-form" align="end" gap="sm" wrap="wrap">
                        <TextInput label="Endpoint vLLM" placeholder="https://..." value={aiSettings.endpoint || ''} onChange={(event) => setAiSettings({ ...aiSettings, endpoint: event.currentTarget.value })} style={{ flex: '2 1 280px' }} />
                        <TextInput label="Токен" type="password" value={aiSettings.api_key || ''} onChange={(event) => setAiSettings({ ...aiSettings, api_key: event.currentTarget.value })} style={{ flex: '1 1 240px' }} />
                        <Stack gap={4} style={{ flex: '1 1 300px' }}>
                          <TextInput label="Модель" placeholder="имя модели vLLM" value={aiSettings.model || ''} onChange={(event) => setAiSettings({ ...aiSettings, model: event.currentTarget.value })} />
                          {gpuModels.length ? <NativeSelect label="Модель с GPU" data={[{ value: '', label: 'Выберите загруженную модель' }, ...gpuModels.map((model) => ({ value: model.id, label: `${model.id}${model.workers != null ? ` · GPU: ${model.workers}` : ''}` }))]} value={gpuModels.some((model) => model.id === aiSettings.model) ? aiSettings.model || '' : ''} onChange={(event) => setAiSettings({ ...aiSettings, model: event.currentTarget.value })} /> : null}
                          <Button size="xs" variant="light" loading={loadingGpuModels} onClick={() => void loadGpuModels()}>Подгрузить модели с GPU</Button>
                        </Stack>
                        <TextInput label="Assistant ID (необязательно)" value={aiSettings.assistant_id || ''} onChange={(event) => setAiSettings({ ...aiSettings, assistant_id: event.currentTarget.value })} style={{ flex: '1 1 220px' }} />
                        <NumberInput label="Температура" min={0} max={1} step={0.1} value={Number(aiSettings.temperature || 0.1)} onChange={(value) => setAiSettings({ ...aiSettings, temperature: String(value ?? 0.1) })} w={130} />
                        <NumberInput label="Макс. токены на часть" description="Бюджет ответа JSON с оценками; рекомендуется 3000–4000" min={256} max={8000} value={aiSettings.max_tokens} onChange={(value) => setAiSettings({ ...aiSettings, max_tokens: Number(value) || 4000 })} w={170} />
                        <NumberInput label="Размер батча" min={1} max={20} value={aiSettings.batch_size} onChange={(value) => setAiSettings({ ...aiSettings, batch_size: Number(value) || 1 })} w={130} />
                      </Group>
                      <Accordion variant="separated"><Accordion.Item value="prompt"><Accordion.Control>Промт AI-проверки операторов</Accordion.Control><Accordion.Panel><Textarea label="Промт AI-проверки операторов" minRows={4} maxRows={10} autosize value={aiSettings.prompt} onChange={(event) => setAiSettings({ ...aiSettings, prompt: event.currentTarget.value })} /></Accordion.Panel></Accordion.Item></Accordion>
                      <Text size="xs" c="dimmed">Когда «Кого проверяем» = оператор и включена «Проверка АИ».</Text>
                      <Accordion variant="separated"><Accordion.Item value="summary_prompt"><Accordion.Control>Промт общего заключения по операторам</Accordion.Control><Accordion.Panel><Textarea label="Промт общего заключения по операторам" description="После завершения выборки по оператору." minRows={4} maxRows={10} autosize value={aiSettings.summary_prompt} onChange={(event) => setAiSettings({ ...aiSettings, summary_prompt: event.currentTarget.value })} /></Accordion.Panel></Accordion.Item></Accordion>
                      <Accordion variant="separated"><Accordion.Item value="prompt_ai"><Accordion.Control>Промт AI-проверки Мозгобота</Accordion.Control><Accordion.Panel><Textarea label="Промт AI-проверки Мозгобота" minRows={4} maxRows={10} autosize value={aiSettings.prompt_ai || ''} onChange={(event) => setAiSettings({ ...aiSettings, prompt_ai: event.currentTarget.value })} /></Accordion.Panel></Accordion.Item></Accordion>
                      <Text size="xs" c="dimmed">Отдельный промт: следование инструкциям, сбор данных, handoff. Можно {'{criteria}'}.</Text>
                      <Accordion variant="separated"><Accordion.Item value="summary_prompt_ai"><Accordion.Control>Промт общего заключения по Мозгоботу</Accordion.Control><Accordion.Panel><Textarea label="Промт общего заключения по Мозгоботу" description="После завершения выборки по Мозгоботу." minRows={4} maxRows={10} autosize value={aiSettings.summary_prompt_ai || ''} onChange={(event) => setAiSettings({ ...aiSettings, summary_prompt_ai: event.currentTarget.value })} /></Accordion.Panel></Accordion.Item></Accordion>
                      <Group justify="space-between" align="center">
                        {aiSettings.assistant_sync === 'failed' ? <Text size="xs" c="orange">Последняя синхронизация Assistant: {aiSettings.assistant_error || 'ошибка'}</Text> : <Text size="xs" c="dimmed">Критерии подставляются в промт при запуске проверки.</Text>}
                        <Button loading={busy} onClick={() => void saveAiSettings()}>
                          Сохранить общие AI-настройки
                        </Button>
                      </Group>
                    </Stack>
                  ) : <InlineLoader label="Загрузка общих AI-настроек" />}
                </Card>
                  </Tabs.Panel>
                </Tabs>
              </Stack>
            ) : <InlineLoader label="Загрузка настроек" />}
          </Tabs.Panel>
        </Tabs>
        <Modal opened={startConfirmation} onClose={() => { if (!busy) setStartConfirmation(false) }} closeOnClickOutside={!busy} closeOnEscape={!busy} withCloseButton={!busy} title="Подтверждение запуска проверки" size="lg" centered>
          <Stack gap="sm">
            {busy && !startPreview ? <InlineLoader label="Подсчет обращений по выбранным параметрам" /> : null}
            {previewChanged ? <Alert color="orange">Количество обращений изменилось. Проверьте новый расчет и подтвердите запуск повторно.</Alert> : null}
            {startError ? <Alert color="red">{startError}</Alert> : null}
            {startPreview ? (
              <>
                <Box className="quality-start-summary">
                  <Text size="xs" c="dimmed">Кто проверяет</Text><Text size="sm">{startPreview.reviewer_name}</Text>
                  <Text size="xs" c="dimmed">Кого проверяем</Text><Text size="sm">{startPreview.target_label}</Text>
                  <Text size="xs" c="dimmed">Проекты</Text><Text size="sm">{startPreview.project_label}</Text>
                  <Text size="xs" c="dimmed">Период (МСК)</Text><Text size="sm">{startPreview.period_label}<br />{formatQualityPreviewTime(startPreview.period_start)} — {formatQualityPreviewTime(new Date(new Date(startPreview.period_end).getTime() - 1).toISOString())}</Text>
                  <Text size="xs" c="dimmed">Способ оценки</Text><Text size="sm">{startPreview.evaluation_mode === 'ai' ? 'AI-проверка' : 'Ручная проверка'}</Text>
                  {startPreview.anonymous_role_filters.length ? <><Text size="xs" c="dimmed">Роли в выборке</Text><Text size="sm">{qualityAnonymousRolesLabel(startPreview.anonymous_role_filters)}</Text></> : null}
                  <Text size="xs" c="dimmed">Подходящих обращений</Text><Text size="sm">{startPreview.total_candidates.toLocaleString('ru-RU')}</Text>
                  <Text size="xs" c="dimmed">В проверке</Text><Text size="sm" fw={700} data-quality-preview-count>{startPreview.sample_mode === 'percent' ? `${startPreview.sample_percent}% (${startPreview.sample_size.toLocaleString('ru-RU')} обращений)` : `${startPreview.sample_size.toLocaleString('ru-RU')} обращений`}</Text>
                  {pendingStart?.sample_mode === 'count' ? <><Text size="xs" c="dimmed">Запрошено</Text><Text size="sm">{pendingStart.sample_count?.toLocaleString('ru-RU')} обращений</Text></> : null}
                  {startPreview.ai_batch_size ? <><Text size="xs" c="dimmed">Размер AI-батча</Text><Text size="sm">{startPreview.ai_batch_size}</Text></> : null}
                </Box>
                {pendingStart?.evaluation_mode === 'ai' && Object.entries(pendingStart.ai_project_instructions || {}).some(([, value]) => value.trim()) ? <Box><Text size="sm" fw={600}>Особые условия проектов</Text>{Object.entries(pendingStart.ai_project_instructions || {}).filter(([, value]) => value.trim()).map(([id, value]) => <Text key={id} size="xs" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{projects.find((project) => project.id === id)?.name || id}: {value}</Text>)}</Box> : null}
                {startPreview.reason ? <Alert color="orange">{startPreview.reason}</Alert> : null}
                <Text size="xs" c="dimmed">Обращения будут случайно отобраны только после подтверждения запуска.</Text>
              </>
            ) : null}
            <Group justify="flex-end" gap="xs">
              <Button variant="default" disabled={busy} onClick={() => setStartConfirmation(false)}>Отмена</Button>
              {startError && !startPreview ? <Button variant="light" disabled={busy} onClick={() => void startSession()}>Повторить расчет</Button> : null}
              <Button loading={busy} disabled={!startPreview?.can_start || Boolean(startError)} onClick={() => void confirmStartSession()}>Подтвердить и запустить</Button>
            </Group>
          </Stack>
        </Modal>
        <Modal
          opened={Boolean(standardPreview)}
          onClose={() => setStandardPreview(null)}
          title={standardPreviewData?.title || 'Стандарт критериев'}
          size="xl"
          centered
        >
          {standardPreviewData ? (
            <Stack gap="sm">
              <Group gap="xs" wrap="wrap">
                <Badge variant="light">Текущих: {standardPreviewData.current.length}</Badge>
                <Badge variant="light" color="blue">В стандарте: {standardPreviewData.standard.length}</Badge>
                <Badge variant="light" color="orange">
                  Отличий: {standardPreviewData.rows.filter((row) => row.status.label !== 'Без изменений').length}
                </Badge>
              </Group>
              <ScrollArea.Autosize mah={520} type="auto" offsetScrollbars>
                <Table striped highlightOnHover verticalSpacing="xs" miw={860}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 120 }}>Статус</Table.Th>
                      <Table.Th>Сейчас</Table.Th>
                      <Table.Th>Стандарт</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {standardPreviewData.rows.map((row) => (
                      <Table.Tr key={row.key}>
                        <Table.Td>
                          <Badge color={row.status.color} variant="light">
                            {row.status.label}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={700}>{row.current?.label || row.key}</Text>
                          <Text size="xs" c="dimmed">{row.current?.key || 'нет в текущем списке'}</Text>
                          <Text size="xs">{criterionShortDetails(row.current)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={700}>{row.standard?.label || row.key}</Text>
                          <Text size="xs" c="dimmed">{row.standard?.key || 'нет в стандарте'}</Text>
                          <Text size="xs">{criterionShortDetails(row.standard)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea.Autosize>
              <Group justify="space-between" gap="xs">
                <Text size="xs" c="dimmed">
                  Просмотр не меняет критерии. Для применения стандарта подтвердите сброс.
                </Text>
                <Group gap="xs">
                  <Button variant="default" onClick={() => setStandardPreview(null)}>
                    Закрыть
                  </Button>
                  <Button
                    color="orange"
                    leftSection={<IconRotateClockwise size={16} />}
                    loading={busy}
                    onClick={() => {
                      const target = standardPreview
                      setStandardPreview(null)
                      if (target) resetCriteriaToDefaults(target)
                    }}
                  >
                    Вернуть к этому стандарту
                  </Button>
                </Group>
              </Group>
            </Stack>
          ) : null}
        </Modal>
        <QualityReportDetailModal
          session={reportDetail}
          loading={reportDetailLoading}
          criteria={criteria}
          exportingMode={reportExportingMode}
          onDownload={(mode) => void downloadReport(mode)}
          onOpenTicket={openTicketFromIncorrectResponse}
          onClose={closeReportDetail}
        />
      </Stack>
    </PageShell>
  )
}
